import type { ProductVariation, VariationSave } from "@shared/variation";
import type { AdCouponPreview } from "@shared/adCoupon";
import { api } from "./api";
import type { AdminMcpKeyCreated, AdminMcpOverview } from "@shared/mcpAdmin";
import type { AdminMcpKeyCreateInput } from "@shared/schemas";
import type { CloneCampaignCard, CloneResult, CloneTargetsView } from "@shared/adsClone";
import type {
  AuctionView,
  ForYouResponse,
  IklanPlacement,
  IklanSettings,
  LiftReport,
  MonetizeOffer,
  MonetizeCampaign,
  BulkResult,
  MonetizeOfferInput,
  MonetizeProduct,
  MonetizeOwner,
  MonetizeStore,
  ManagedOverview,
  MonetizeOverview,
  WalletView,
  IklanSlot,
} from "@shared/iklan";
import type { Breakdown, ManagerView, MonetizeFunnel } from "@shared/iklanManager";
import type { CampaignInput, CampaignOptions, RejectReason, ReviewItem } from "@shared/iklanCampaign";
import type { ClaimedAdCoupon } from "@shared/adCoupon";
import type { CreditCodeView, CreditMode, HqMeter } from "@shared/iklanKredit";
import type { TopupMethod } from "@shared/iklan";
import type { FypRankSettings, FypRankView } from "@shared/fypRank";
import type { FypReport, FypReportDays } from "@shared/fypReport";
import type { FypSource } from "@shared/fypSource";
import type { FypAiSettings, FypAiView } from "@shared/fypAi";
import { CW_CLIENT_WAIT_MS } from "@shared/fypCwTiming";
import type { StoreLinkStats } from "@shared/productLink";
import type {
  ApiAuditEntry,
  ApiKeyCreated,
  ApiKeyCreateInput,
  ApiKeySummary,
  OAuthAuthorizeCheck,
  WebhookDeliverySummary,
  WebhookEndpointCreated,
  WebhookEndpointCreateInput,
  WebhookEndpointSummary,
  WebhookTargets,
  WebhookEndpointUpdateInput,
  WebhookEvent,
  WebhookTestResult,
} from "@shared/integrations";
import { maybeOptimizeImageFormData } from "./imageOptimize";
import { CHAT_CLIENT_WAIT_MS } from "@shared/iklanChatTiming";
import { storeLandingPath } from "@shared/earlyStore";
import type {
  AdminAffiliatesResponse,
  AdminImpersonateResult,
  AdminCouponRedemption,
  AdminOrdersPage,
  AdminOverview,
  AdminPaymentsOverview,
  AdminPlanRevenue,
  AdminSellerDetail,
  AdminSubscriptionOrdersPage,
  AdminSiteOwnerLookup,
  AdminDomainAudit,
  AdminDomainMigration,
  DomainHttpProbe,
  AdminCfAccount,
  AdminSiteDetail,
  AdminSiteGateways,
  AdminSitePixel,
  AdminImportablePage,
  AdminImportPageResult,
  AdminBulkSyncResult,
  AdminSupportTicket,
  AdminSupportTicketDetail,
  AdminAuditList,
  AdminContentList,
  AdminDomainList,
  AdminSellerList,
  AdminSiteList,
  AdminSubscribedSellerList,
} from "@shared/admin";
import type {
  AccountAccess,
  AccountCertificate,
  AccountHubSummary,
  AccountShareLinks,
  AccountOrder,
  AccountProfile,
  CertificateData,
  AbandonedCheckoutList,
  CartRecoveryResume,
  BonusClaim,
  BonusOffer,
  BonusOverview,
  BonusPagesOverview,
  BonusRewardBlock,
  BonusRewardProduct,
  MyReviewCoupon,
  CommunityComment,
  CommunityAbout,
  CommunityCategory,
  CommunityLevel,
  CommunityLevelReward,
  CommunityFeed,
  CommunityManageProduct,
  CommunityPageScope,
  CommunityStoreScope,
  CommunityModerationItem,
  CommunityPendingPost,
  CommunityPost,
  CommunityProductRef,
  AnalyticsBreakdown,
  AnalyticsGranularity,
  AnalyticsSeries,
  AnalyticsSummary,
  DateRangePreset,
  ClaimAccountResult,
  EnsureAccessResult,
  CourseCard,
  CustomerChangeEmailResult,
  CustomerDetail,
  CustomerSummary,
  DeliveryFile,
  DigitalAccess,
  DiscountCode,
  DiscountPreview,
  RedirectResolution,
  StoreRedirect,
  Handle,
  HandleSales,
  LmsContent,
  MarketplaceProduct,
  MarketplacePageItem,
  MarketplaceResult,
  MarketplaceSort,
  Order,
  SellerCustomer,
  MyPlan,
  LiveFeed,
  LivePulse,
  OverviewStats,
  Page,
  PlanConfig,
  PlanUsage,
  PlanProject,
  PlatformCoupon,
  PlatformSettings,
  AdminPlatformSettings,
  HomepageChoice,
  LandingLibraryPage,
  Product,
  MyStore,
  Store,
  StoreMember,
  StoreInviteResult,
  PublicAccess,
  PublicOrderStatus,
  PublicPage,
  PublicRecentOrder,
  PublicResellerPlan,
  PublicStore,
  SubdomainCheck,
  SafeUserSettings,
  CertificateSignerSettings,
  SellerAffiliateSettings,
  SellerAffiliateDashboard,
  Affiliate,
  AffiliateMe,
  AffiliateEarnings,
  AffiliatePortalSummary,
  AffiliateLedgerPage,
  AffiliateJoinState,
  AffiliateLinkCard,
  AffiliatePageCard,
  AffiliateStoreOffer,
  AffiliateTrafficRow,
  AffiliateBankAccount,
  AffiliateCouponRow,
  AffiliateProductSettings,
  AffiliateReferralRow,
  AffiliateOwedRow,
  AffiliateProgramNudges,
  AffiliateShareOffer,
  AffiliateShareState,
  AffiliateCompletion,
  AffiliatePayoutRow,
  AffiliateAsset,
  AffiliateReportPoint,
  ShopStore,
  ProductSalesPage,
  PublicProductReviews,
  PurgeResult,
  SubscriptionCheckoutOptions,
  SubscriptionPayment,
  SupportMessage,
  SupportTicket,
  SupportTicketDetail,
  LiveLink,
  LiveSessionInfo,
  LiveFormat,
  LiveBag,
  LiveBagItem,
  PublicLive,
  LiveChatTicket,
  LiveCameraStatus,
  LiveReplayState,
  LiveVideoSource,
  BayarcashPortal,
  ClassRoomDoor,
  ClassRoomEntry,
  BilikDashboard,
  BilikSessionReport,
  BilikAfterView,
  LandingDomain,
  LandingDomainsAddResult,
  LandingDomainsView,
} from "@shared/types";
import type { FypHomeState } from "@shared/fypHome";
import type {
  CartRecoverySettings,
  FupStage,
  FupStats,
  PendingFollowupList,
} from "@shared/cartRecovery";
import type { OrderView, OrdersListPage, OrdersPageSize, OrdersSummary } from "@shared/orders";
import type { FypComment, FypDashboard, FypLink, FypPost, FypPostStatus, FypSellerComment, FypStats, FypTap, PublicFyp, PublicFypProfile, PublicFypRecentBuyer, PublicFypReview, PublicFypReviewsPage } from "@shared/types";
import type { InsightsHeatPoint, InsightsOverview, InsightsVersion } from "@shared/insights";
import type { AiIntensity } from "@shared/aiEvidence";

/** The evidence pack the AIs read, and its rough token count at every intensity. */
export type InsightsEvidence = {
  intensity: AiIntensity;
  text: string;
  tokens: Record<AiIntensity, number>;
  version: string;
  sessions: number;
};

/** Which version of a copy Analitik Halaman reads, and over what window. */
export type InsightsView = { version?: string | null; span?: "window" | "change"; since?: string | null; julat?: string };
const insightsViewQuery = (v: InsightsView) =>
  (v.version && v.version !== "a" ? `&v=${encodeURIComponent(v.version)}&span=${v.span ?? "window"}` : "") +
  (v.since ? `&since=${encodeURIComponent(v.since)}` : "") +
  (v.julat ? `&${v.julat}` : "");
import type { AdsOverview, AdsSettingsView, AiProvider } from "@shared/ads";
import type { FunnelAiActionView, FunnelAiRunView, FunnelAiSettingsView, FunnelRetiredView, FunnelScoreboard } from "@shared/funnelAi";

/** Keputusan AI on one funnel, as every AI route answers. */
export type FunnelAiState = {
  run: FunnelAiRunView | null;
  actions: FunnelAiActionView[];
  view: FunnelAiSettingsView;
  retired: FunnelRetiredView[];
  board: FunnelScoreboard;
};
import type { AdsActionKind, AdsActionParams, AdsActionRecord, AdsDecisionsView } from "@shared/adsDecisions";
import type { AdsAudience, AdsAudienceInput, AdsPixel } from "@shared/adsAudiences";
import type { MetaRuleSuggestion, MetaRuleView, MetaRulesView } from "@shared/adsMetaRules";
import type { RuleEdit } from "@shared/adsMetaRuleSpec";
import type { AdsAiSettingsInput, AdsCloneInput, AdsExternalInput, AdsLaunchInput, AdsNewAdsInput, AdsRulesInput, AdsSkillsInput, AdsTakeoverInput } from "@shared/schemas";
import type { KelasBulkInput, KelasNewBatchInput, KelasWhatsappInput } from "@shared/schemas";
import type { KelasBulkResult } from "@shared/kelasBulk";
import type { KelasBatchPlan, KelasNewBatchResult, KelasWhatsappPlan, KelasWhatsappResult } from "@shared/kelasBatch";
import type { KelasGuests } from "@shared/types";
import type { AdsAiRunView } from "@shared/adsPlaybook";
import type { PulseView } from "@shared/adsPulse";
import type { CashflowCube, CashflowProduct, CashflowPulse, CashflowRole } from "@shared/cashflow";
import type { LegacyReleaseReport, SellerWebhookInfo } from "@shared/legacyWebhook";
import type {
  CashflowAiSettingsInput,
  CashflowCostsInput,
  CashflowSettingsInput,
  CashflowSourceCreateInput,
  CashflowSourcePatchInput,
  CashflowUpsellCycleInput,
  CashflowUpsellNewInput,
} from "@shared/schemas";
import type { CashflowSourceView, HookDelivery } from "@shared/cashflowHooks";
import type { MetaConnectStatus } from "@shared/metaConnect";
import type { CashflowInsights } from "@shared/cashflowInsights";
import type { CashflowHours } from "@shared/cashflowHours";
import type { CashflowAiView } from "@shared/cashflowAi";
import type { CashflowActionView } from "@shared/cashflowActions";
import type { UpsellPulse, UpsellView } from "@shared/cashflowUpsell";
import type { AdsLearningView } from "@shared/adsLearning";
import type { TextVariation } from "@shared/adsCreative";
import type { AccountImage, AdDraft, AdsCreativeInsight, WinningRow } from "@shared/adsCreativeAi";
import type { PageVariant, VariantOpResult } from "@shared/pageVariant";
import type {
  ArmKey,
  FunnelCampaignDays,
  FunnelCampaignsReport,
  FunnelGoal,
  FunnelMethod,
  FunnelOptimizeResult,
  FunnelPageOption,
  FunnelPublishTarget,
  FunnelReport,
  FunnelSourceOption,
  FunnelStoreOption,
  FunnelWindow,
} from "@shared/funnel";
import type { FunnelFypOptions } from "@shared/funnelFyp";
import type { FunnelLayoutState, Layout } from "@shared/funnelCanvas";
import type { AdLinksResponse } from "@shared/adLinks";
import type { CustomersListPage } from "@shared/customers";
import type { ListPageSize } from "@shared/listPaging";
import type {
  CartRecoverySettingsInput,
  CartRecoveryTestInput,
  FunnelAiExternalInput,
  FunnelAiSettingsInput,
  FunnelAiTakeoverInput,
  VariantUpdateInput,
} from "@shared/schemas";
import type {
  AdminResellerRow,
  AdminResellerDetail,
  AdminResellerChannel,
  AdminResellerBulkInput,
} from "@shared/reseller";
import type {
  AdminAssignPlanInput,
  AdminBulkAssignPlanInput,
  AdminSiteCreateInput,
  AdminSiteUpdateInput,
  AdminPixelUpdateInput,
  AdminGatewayUpdateInput,
  AdminBulkDeleteInput,
  AdminDomainCreateInput,
  AdminCfAccountCreateInput,
  AdminCfAccountUpdateInput,
  AdminDomainLinkInput,
  AdminDomainBulkDeleteInput,
  StoreMemberInviteInput,
  AdminAffiliateStatusInput,
  AdminAffiliatePayoutInput,
  AffiliateSettingsInput,
  AffiliatePayoutInput,
  AffiliateBankInput,
  AffiliateBankReminderInput,
  AffiliateContactInput,
  AffiliatePayoutRequestInput,
  AffiliateCouponInput,
  AffiliateProductSettingInput,
  AffiliateReferralActionInput,
  AffiliatePayoutGenerateInput,
  AffiliatePayoutStatusInput,
  AffiliateAssetInput,
  AffiliateTemplateInput,
  AffiliatePixelInput,
  CreateBillInput,
  CustomerAssignPackageInput,
  CustomerChangeEmailInput,
  CustomerUpdateInput,
  DigitalAccessCreateInput,
  DiscountCodeCreateInput,
  DiscountCodeUpdateInput,
  DiscountPreviewInput,
  AdCouponPreviewInput,
  RedirectCreateInput,
  LandingDomainCreateInput,
  LandingDomainUpdateInput,
  RedirectUpdateInput,
  HandleCreateInput,
  HandleUpdateInput,
  OrderCreateInput,
  OrderUpdateInput,
  PageCreateInput,
  PageUpdateInput,
  PlanConfigCreateBody,
  PlanConfigUpdateInput,
  PlanProjectCreateBody,
  PlanProjectDuplicateBody,
  PlanProjectUpdateInput,
  PlatformCouponCreateInput,
  PlatformCouponUpdateInput,
  PlatformSettingsUpdateInput,
  ProductCreateInput,
  ProductUpdateInput,
  SettingsUpdateInput,
  CertificateSignerInput,
  BayarcashPortalsInput,
  StoreUpdateInput,
  SubscribeInput,
  PublicSubscribeInput,
  PublicSubscribeCodeInput,
  CouponReviewInput,
  TrackEventInput,
  TrackProductViewInput,
  TrackAbandonedInput,
  SupportTicketCreateInput,
  SupportMessageInput,
  SupportStatusInput,
  WpImportSaveInput,
  WpImportAutoInput,
  WpImportPerfInput,
  WpImportTextInput,
  WpImportTextSlotInput,
} from "@shared/schemas";

/** Seller plan view returned by GET /plans/me (plan + billing history). */
export type MyPlanWithHistory = MyPlan & { history: SubscriptionPayment[] };

/** Where "Jana versi B" gets its Claude key from; the key itself is never sent. */
export interface InsightsAiKeyStatus {
  source: "dashboard" | "secret" | null;
  hint: string | null;
  updatedAt: string | null;
}

/** How a funnel shares out new visitors, as the console sends it. */
export interface FunnelTrafficBody {
  method: FunnelMethod;
  weights: number[];
  floorPct: number;
  goal: FunnelGoal;
  autoStop: boolean;
}

/** Analitik Halaman's Versi B screen: the stored edits and how each one lands. */
/** One version previewed read-only beside A (GET /admin/insights/:slug/versions/:name). */
export interface PageVersionView {
  variant: PageVariant | null;
  results: VariantOpResult[];
  sections: { key: string; heading: string }[];
  previewA: string;
  previewV: string | null;
}

export interface PageVariantView {
  variant: PageVariant | null;
  results: VariantOpResult[];
  sections: { key: string; heading: string }[];
  aiKey: InsightsAiKeyStatus;
  model: string;
  previewA: string;
  previewB: string | null;
}

/** Native WP-import job status (mirrors server jobStatus.ts JobStatus). */
export type WpImportJobStatus = {
  jobId: string;
  slug: string;
  sourceUrl: string;
  stage: "queued" | "rendering" | "localizing" | "verifying" | "done" | "error";
  done: boolean;
  ok: boolean;
  message: string;
  previewUrl?: string;
  verdict?: "PASS" | "FAIL";
  findings?: { severity: string; check: string; detail: string }[];
  stats?: Record<string, unknown>;
  error?: string;
  updatedAt: string;
};

/** A WordPress copy produced by the native importer, recorded for reuse. */
export type WpImport = {
  id: string;
  sourceUrl: string;
  slug: string;
  cdnUrl: string;
  verdict: string | null;
  removeShapeDividers: boolean;
  title: string | null;
  lastStoreId: string | null;
  /** 'wp' = imported from WordPress; 'custom' = hand-built sales page ("Versi optimize"). */
  kind: "wp" | "custom";
  hiddenAt: string | null;
  createdAt: string;
  updatedAt: string;
};

export interface StartSubscriptionResult {
  subscriptionId: string;
  /** ToyyibPay bill code when applicable; absent for the multi-gateway paths. */
  billCode?: string;
  paymentUrl: string;
}

export interface CreateBillResult {
  orderId: string;
  billCode: string;
  paymentUrl: string;
}

type WithProduct<T> = T & { product: { name: string } | null };

/** Custom-range bounds (YYYY-MM-DD, MYT) — required only for preset "custom". */
/** One span of the affiliate report: referrals, and commission that stood up or was voided. */
export interface AffiliateReportTotals {
  referrals: number;
  approved_cents: number;
  rejected_cents: number;
}

export interface CustomRange {
  start: string;
  end: string;
}

export interface OrdersListParams {
  view?: OrderView;
  q?: string;
  /** A `payment_gateway` value; "" means every gateway. */
  gateway?: string;
  page?: number;
  /** A row count, or ORDERS_LIMIT_ALL for every matching row. */
  limit?: OrdersPageSize | number;
}

/** `paged` is off for the export endpoint, which deliberately ignores paging. */
function ordersQuery(p: OrdersListParams, paged = true): string {
  const params = new URLSearchParams();
  if (p.view && p.view !== "semua") params.set("view", p.view);
  if (p.q?.trim()) params.set("q", p.q.trim());
  if (p.gateway?.trim()) params.set("gateway", p.gateway.trim());
  if (paged && p.page && p.page > 1) params.set("page", String(p.page));
  if (paged && p.limit) params.set("limit", String(p.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export interface CustomersListParams {
  q?: string;
  page?: number;
  /** A row count, or LIST_LIMIT_ALL for every matching row. */
  limit?: ListPageSize | number;
}

/** `paged` is off for the export endpoint, which deliberately ignores paging. */
function customersQuery(p: CustomersListParams, paged = true): string {
  const params = new URLSearchParams();
  if (p.q?.trim()) params.set("q", p.q.trim());
  if (paged && p.page && p.page > 1) params.set("page", String(p.page));
  if (paged && p.limit) params.set("limit", String(p.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

function analyticsQuery(preset: DateRangePreset, custom?: CustomRange): string {
  const params = new URLSearchParams({ preset });
  if (preset === "custom" && custom) {
    params.set("start", custom.start);
    params.set("end", custom.end);
  }
  return params.toString();
}

/** A post as the FYP editor sends it: videos kept by id, or new ones. */
export type FypPostBody = {
  caption: string;
  /** Text options as typed (109O S4); the server drops empty ones and works out ids. */
  texts?: { primary: string[]; headline: string[]; desc: string[] };
  /** The words "AI isi semua" wrote that are still as it wrote them (110B). */
  aiFill?: string[];
  status: FypPostStatus;
  pinned: boolean;
  productIds: string[];
  tap: FypTap;
  reviewsOn: boolean;
  videos: ({ id: string } | { youtube: string } | { upload: FypUploadRef } | { slides: FypSlideRef[]; audio?: string | null })[];
};

/** One picture of a slide card, kept by the worker (109O). */
export type FypSlideRef = { file: string; width: number; height: number };

/** An upload the worker has checked and kept, named in a post. */
export type FypUploadRef = { file: string; poster: string | null; width: number; height: number; durationMs: number | null };

const RECENT_ORDERS_MEMO_MS = 20_000;
const recentOrdersMemo = new Map<string, { at: number; promise: Promise<{ orders: PublicRecentOrder[] }> }>();

/** POST/PUT /api/bilik/webinar */
export interface WebinarBody {
  title: string;
  startsAt: string;
  durationMin: number;
  access: "register" | "open" | "invite";
  details: import("@shared/bilik").WebinarDetails;
}

export const resources = {
  overview: () => api.get<OverviewStats>("/overview"),
  liveFeed: () => api.get<LiveFeed>("/live/feed"),
  liveStart: (slug?: string, format: LiveFormat = "analytics") =>
    api.post<{ session: LiveSessionInfo; link: LiveLink }>("/live/session/start", slug ? { slug, format } : { format }),
  liveFormat: (format: LiveFormat) => api.patch<{ session: LiveSessionInfo }>("/live/session/format", { format }),
  liveBagOptions: () => api.get<{ items: LiveBagItem[] }>("/live/bag-options"),
  liveBagSave: (items: string[], pinned: string | null) => api.put<{ bag: LiveBag | null }>("/live/bag", { items, pinned }),
  liveEnd: () => api.post<{ ended: boolean }>("/live/session/end", {}),
  liveLink: (patch: { slug?: string; showProducts?: boolean }) => api.patch<{ link: LiveLink }>("/live/link", patch),
  liveSlugSuggest: () => api.get<{ slug: string }>("/live/slug-suggest"),
  liveSlugCheck: (slug: string) =>
    api.get<{ ok: boolean; reason?: string }>(`/live/slug-check?slug=${encodeURIComponent(slug)}`),
  bilik: {
    page: () => api.get<BilikDashboard>("/bilik"),
    segera: () => api.post<{ id: string; code: string }>("/bilik/segera", {}),
    personal: (code: string) => api.put<{ id: string; code: string }>("/bilik/peribadi", { code }),
    access: (id: string, access: "open" | "invite" | "register") => api.patch<{ id: string; access: string }>(`/bilik/${id}`, { access }),
    invite: (id: string, emails: string) => api.post<{ invited: number; bad: string[] }>(`/bilik/${id}/invites`, { emails }),
    remove: (id: string) => api.delete<{ id: string }>(`/bilik/${id}`),
    offerOptions: () => api.get<{ items: LiveBagItem[] }>("/bilik/offer-options"),
    session: (id: string) => api.get<BilikSessionReport>(`/bilik/sessions/${encodeURIComponent(id)}`),
    uploadCover: async (file: FormData) => api.upload<{ key: string }>("/bilik/cover", await maybeOptimizeImageFormData(file)),
    toCustomers: (id: string) => api.post<{ added: number; total: number }>(`/bilik/${id}/to-customers`, {}),
    saveWebinar: (id: string | null, body: WebinarBody) =>
      id ? api.put<{ id: string; code: string }>(`/bilik/webinar/${id}`, body) : api.post<{ id: string; code: string }>("/bilik/webinar", body),
  },
  fyp: () => api.get<FypDashboard>("/fyp"),
  fypLink: (slug?: string) => api.patch<{ link: FypLink }>("/fyp/link", slug ? { slug } : {}),
  fypSlugSuggest: () => api.get<{ slug: string }>("/fyp/slug-suggest"),
  fypSlugCheck: (slug: string) =>
    api.get<{ ok: boolean; reason?: string }>(`/fyp/slug-check?slug=${encodeURIComponent(slug)}`),
  fypBagOptions: () => api.get<{ items: LiveBagItem[] }>("/fyp/bag-options"),
  fypSavePost: (id: string | null, body: FypPostBody) =>
    id ? api.put<{ post: FypPost }>(`/fyp/posts/${id}`, body) : api.post<{ post: FypPost }>("/fyp/posts", body),
  fypDeletePost: (id: string) => api.delete<{ ok: boolean }>(`/fyp/posts/${id}`),
  fypUploadStart: (body: { size: number; type: string }) =>
    api.post<{ file: string; uploadId: string; partBytes: number }>("/fyp/upload/start", body),
  fypUploadPart: (file: string, uploadId: string, part: number, bytes: Blob, signal?: AbortSignal) =>
    api.putBinary<{ part: number; etag: string }>(
      `/fyp/upload/part?${new URLSearchParams({ file, uploadId, part: String(part) })}`,
      bytes,
      signal,
    ),
  fypUploadComplete: (body: { file: string; uploadId: string; parts: { part: number; etag: string }[] }) =>
    api.post<{ file: string; size: number; width: number; height: number; level: number }>("/fyp/upload/complete", body),
  fypUploadAbort: (file: string, uploadId: string) => api.post<{ ok: boolean }>("/fyp/upload/abort", { file, uploadId }),
  fypUploadPoster: (jpeg: Blob) => api.putBinary<{ file: string }>("/fyp/upload/poster", jpeg),
  fypUploadImage: (image: Blob) => api.putBinary<{ file: string }>("/fyp/upload/image", image),
  fypUploadAudio: (mp3: Blob) => api.putBinary<{ file: string }>("/fyp/upload/audio", mp3),
  fypTextMark: (postId: string, tid: string, body: { lock?: boolean; retired?: false }) => api.put<{ texts: unknown }>(`/fyp/texts/${postId}/${tid}`, body),
  fypTextEngine: (on: boolean) => api.put<{ on: boolean }>("/fyp/texts/settings", { on }),
  fypLiveMix: (body: { on: boolean; order: "auto" | "live" | "video" }) => api.put<{ link: FypLink | null }>("/fyp/live-mix", body),
  publicFypLive: (slug: string) => api.get<{ live: import("@shared/fypLiveMix").PublicFypLive | null }>(`/fyp-public/${encodeURIComponent(slug)}/live`, { public: true }),
  fypUploadDelete: (file: string) => api.delete<{ ok: boolean }>(`/fyp/upload?file=${encodeURIComponent(file)}`),
  fypLike: (code: string, vid: string, on: boolean) => api.post<{ likes: number }>("/fyp-public/like", { code, vid, on }, { public: true }),
  shareOffer: (surface: "fyp" | "profil", ref: string) =>
    api.get<AffiliateShareOffer | null>(`/share/offer?surface=${surface}&ref=${encodeURIComponent(ref)}`, { public: true }),
  shareState: (storeId: string) =>
    api.get<AffiliateShareState>(`/affiliates/share-state?storeId=${encodeURIComponent(storeId)}`),
  shareJoin: (storeId: string) =>
    api.post<AffiliateShareState>("/affiliates/share-join", { storeId, acceptTerms: true }),
  shareCode: (email: string, turnstileToken?: string) =>
    api.post<{ sent: boolean }>("/auth/share-code", { email, turnstileToken }, { public: true }),
  affiliateInvitePage: (code: string) =>
    api.get<import("@shared/types").AffiliateInvitePage>(`/affiliate-join/invite/${encodeURIComponent(code)}`, { public: true }),
  affiliateInviteAccept: (code: string) => api.post<AffiliateShareState>("/affiliates/invite-accept", { code }),
  affiliateJoinPage: (handle: string) =>
    api.get<import("@shared/types").AffiliateJoinPage>(`/affiliate-join/${encodeURIComponent(handle)}`, { public: true }),
  kelasRoom: {
    door: (code: string) => api.get<ClassRoomDoor>(`/kelas-room/${encodeURIComponent(code)}`, { public: true }),
    sendCode: (code: string, email: string, turnstileToken?: string) =>
      api.post<{ sent: boolean }>(`/kelas-room/${encodeURIComponent(code)}/code`, { email, turnstileToken }, { public: true }),
    enter: (code: string, body: { pass?: string; guest?: boolean }) =>
      api.post<ClassRoomEntry>(`/kelas-room/${encodeURIComponent(code)}/enter`, body, { optionalAuth: true }),
    after: (code: string, pass: string) => api.get<BilikAfterView>(`/kelas-room/${encodeURIComponent(code)}/after?t=${encodeURIComponent(pass)}`, { public: true }),
    register: (code: string, body: { name: string; email: string; phone: string; turnstileToken?: string }) =>
      api.post<{ pass: string; name: string; created: boolean }>(`/kelas-room/${encodeURIComponent(code)}/register`, body, { public: true }),
  },
  fypShare: (code: string) => api.post<{ ok: boolean }>("/fyp-public/share", { code }, { public: true }),
  fypComments: (code: string, before?: string | null) =>
    api.get<{ comments: FypComment[]; more: boolean; reviews?: PublicFypReview[] }>(
      `/fyp-public/comments/${code}${before ? `?before=${encodeURIComponent(before)}` : ""}`,
      { public: true },
    ),
  fypComment: (code: string, body: { vid: string; body: string; anon?: boolean; turnstileToken?: string }) =>
    api.post<{ comment: FypComment }>(`/fyp-public/comments/${code}`, body, { optionalAuth: true }),
  fypSellerComments: (postId: string) => api.get<{ comments: FypSellerComment[] }>(`/fyp/comments?post=${postId}`),
  fypHideComment: (id: string, hidden: boolean) => api.patch<{ hidden: boolean }>(`/fyp/comments/${id}`, { hidden }),
  fypStats: (days = 30) => api.get<FypStats>(`/fyp/stats?days=${days}`),
  fypRank: () => api.get<FypRankView>("/fyp/rank"),
  fypRankSave: (body: { on?: boolean; holdout?: number; settings?: Partial<FypRankSettings> }) => api.patch<FypRankView>("/fyp/rank", body),
  fypRankRun: () => api.post<FypRankView>("/fyp/rank/run", {}),
  fypRankLock: (code: string, position: number | null) => api.put<FypRankView>("/fyp/rank/lock", { code, position }),
  fypRankUndo: (runId: string) => api.post<FypRankView>("/fyp/rank/undo", { runId }),
  fypReport: (days: FypReportDays, src?: FypSource) => api.get<FypReport>(`/fyp/report?days=${days}${src ? `&src=${src}` : ""}`),
  fypReportEmail: (body: { on: boolean; to: string[] }) => api.put<FypReport>("/fyp/report/email", body),
  fypAi: () => api.get<FypAiView>("/fyp/ai"),
  fypAiSave: (body: Partial<FypAiSettings>) => api.patch<FypAiView>("/fyp/ai", body),
  fypAiRun: () => api.post<FypAiView>("/fyp/ai/run", {}),
  fypAiTags: () => api.post<FypAiView>("/fyp/ai/tags", {}),
  fypAiCard: (id: string, action: "approve" | "reject") => api.post<FypAiView>(`/fyp/ai/cards/${id}`, { action }),
  fypAiTextCard: (id: string, action: "approve" | "reject") => api.post<FypAiView>(`/fyp/ai/text-cards/${id}`, { action }),
  fypCwAi: (body: { postId: string | null; productIds: string[]; texts: Record<"primary" | "headline" | "desc", string[]> }) =>
    api.post<import("@shared/fypCwAi").FypCwResult>("/fyp/cw-ai", body, { timeoutMs: CW_CLIENT_WAIT_MS }),
  publicFyp: (slug: string, opts: { start?: string | null; cursor?: number; arm?: "c" | "r" | null } = {}) => {
    const q = new URLSearchParams();
    if (opts.start) q.set("start", opts.start);
    if (opts.cursor) q.set("cursor", String(opts.cursor));
    if (opts.arm === "c") q.set("fr", "c");
    const qs = q.toString();
    return api.get<PublicFyp>(`/fyp-public/${encodeURIComponent(slug)}${qs ? `?${qs}` : ""}`, { public: true });
  },
  // A funnel's FYP version: only its videos.
  publicFypFunnel: (funnelId: string, arm: string) =>
    api.get<PublicFyp>(`/fyp-public/funnel/${encodeURIComponent(funnelId)}/${encodeURIComponent(arm)}`, { public: true }),
  publicFypRecent: (slug: string) =>
    api.get<{ buyers: PublicFypRecentBuyer[] }>(`/fyp-public/${encodeURIComponent(slug)}/recent`, { public: true }),
  publicFypProfile: (slug: string) => api.get<PublicFypProfile>(`/fyp-public/${encodeURIComponent(slug)}/profile`, { public: true }),
  publicFypReviews: (slug: string, filter: { stars?: number | null; media?: boolean }, page: number) =>
    api.get<PublicFypReviewsPage>(
      `/fyp-public/${encodeURIComponent(slug)}/reviews?${new URLSearchParams({
        ...(filter.media ? { media: "1" } : filter.stars ? { bintang: String(filter.stars) } : {}),
        page: String(page),
      })}`,
      { public: true },
    ),
  publicLive: (slug: string) => api.get<PublicLive>(`/live-public/${encodeURIComponent(slug)}`, { public: true }),
  liveChatTicket: (slug: string, body: { vid: string; anon?: boolean; noLoc?: boolean; turnstileToken?: string }) =>
    api.post<LiveChatTicket>(`/live-public/${encodeURIComponent(slug)}/chat-ticket`, body, { optionalAuth: true }),
  liveChatSay: (text: string) => api.post<{ ok: boolean }>("/live/chat", { text }),
  liveChatDelete: (id: string) => api.post<{ ok: boolean }>("/live/chat/delete", { id }),
  liveChatMute: (key: string) => api.post<{ ok: boolean }>("/live/chat/mute", { key }),
  liveChatState: (on: boolean) => api.post<{ on: boolean }>("/live/chat/state", { on }),
  liveCamera: () => api.get<LiveCameraStatus>("/live/camera"),
  liveCameraPublish: (body: { sdp: string; quality: "jimat" | "hd"; tracks: { mid: string; trackName: "video" | "audio" }[] }) =>
    api.post<{ sdp: string }>("/live/camera/publish", body),
  liveCameraStop: () => api.post<{ on: boolean }>("/live/camera/stop", {}),
  liveReplay: () => api.get<LiveReplayState>("/live/replay"),
  liveReplayPatch: (patch: { source?: LiveVideoSource; loop?: boolean }) => api.patch<LiveReplayState>("/live/replay", patch),
  liveReplayYoutube: (url: string) => api.put<LiveReplayState>("/live/replay/youtube", { url }),
  liveReplayUploadStart: (body: { size: number; type: string }) =>
    api.post<{ file: string; uploadId: string; partBytes: number }>("/live/replay/upload/start", body),
  liveReplayUploadPart: (file: string, uploadId: string, part: number, bytes: Blob, signal?: AbortSignal) =>
    api.putBinary<{ part: number; etag: string }>(
      `/live/replay/upload/part?${new URLSearchParams({ file, uploadId, part: String(part) })}`,
      bytes,
      signal,
    ),
  liveReplayUploadComplete: (body: {
    file: string;
    uploadId: string;
    parts: { part: number; etag: string }[];
    name: string;
    durationSec: number | null;
  }) => api.post<LiveReplayState>("/live/replay/upload/complete", body),
  liveReplayUploadAbort: (file: string, uploadId: string) => api.post<{ ok: boolean }>("/live/replay/upload/abort", { file, uploadId }),
  liveReplayUploadDelete: () => api.delete<LiveReplayState>("/live/replay/upload"),
  liveReplayPlay: () => api.post<LiveReplayState>("/live/replay/play", {}),
  liveReplayStop: () => api.post<LiveReplayState>("/live/replay/stop", {}),
  liveReplayDuration: (durationSec: number) => api.post<LiveReplayState>("/live/replay/duration", { durationSec }),
  liveCameraSubscribe: (slug: string, since?: number | null, fyp?: boolean) =>
    api.post<{ sessionId: string; sdp: string }>(
      `/live-public/${encodeURIComponent(slug)}/camera/subscribe`,
      { ...(since ? { since: Math.floor(since) } : {}), ...(fyp ? { fyp: true } : {}) },
      { public: true },
    ),
  liveCameraSnapshot: (jpeg: Blob) => api.putBinary<{ ok: boolean }>("/live/camera/snapshot", jpeg),
  liveCameraAnswer: (slug: string, sessionId: string, sdp: string) =>
    api.post<{ ok: boolean }>(`/live-public/${encodeURIComponent(slug)}/camera/answer`, { sessionId, sdp }, { public: true }),
  livePulse: (since?: string) =>
    api.get<LivePulse>(`/live/pulse${since ? `?since=${encodeURIComponent(since)}` : ""}`),
  analytics: (preset: DateRangePreset, custom?: CustomRange) =>
    api.get<AnalyticsSummary>(`/analytics?${analyticsQuery(preset, custom)}`),
  analyticsSeries: (
    preset: DateRangePreset,
    granularity: AnalyticsGranularity,
    custom?: CustomRange,
  ) =>
    api.get<AnalyticsSeries>(
      `/analytics/series?${analyticsQuery(preset, custom)}&granularity=${granularity}`,
    ),
  analyticsBreakdown: (preset: DateRangePreset, custom?: CustomRange) =>
    api.get<AnalyticsBreakdown>(`/analytics/breakdown?${analyticsQuery(preset, custom)}`),

  pages: {
    list: () => api.get<Page[]>("/pages"),
    get: (id: string) => api.get<Page>(`/pages/${id}`),
    create: (body: PageCreateInput) => api.post<Page>("/pages", body),
    update: (id: string, body: PageUpdateInput) => api.patch<Page>(`/pages/${id}`, body),
    publish: (id: string) => api.post<Page>(`/pages/${id}/publish`),
    duplicate: (id: string) => api.post<Page>(`/pages/${id}/duplicate`),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/pages/${id}`),
    /** Ordered product ids sold on this page. */
    products: (id: string) => api.get<string[]>(`/pages/${id}/products`),
    /** Replace the page's product set + order (array order = on-page order). */
    setProducts: (id: string, productIds: string[]) =>
      api.put<string[]>(`/pages/${id}/products`, { productIds }),
  },

  handles: {
    list: () => api.get<Handle[]>("/handles"),
    /** Paid-order totals per team, per page, and the default /p/ bucket. */
    sales: () => api.get<HandleSales>("/handles/sales"),
    create: (body: HandleCreateInput) => api.post<Handle>("/handles", body),
    update: (id: string, body: HandleUpdateInput) => api.patch<Handle>(`/handles/${id}`, body),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/handles/${id}`),
    /** Replace the full set of pages assigned to this handle. */
    setPages: (id: string, pageIds: string[]) =>
      api.put<string[]>(`/handles/${id}/pages`, { pageIds }),
  },

  products: {
    list: () => api.get<Product[]>("/products"),
    create: (body: ProductCreateInput) => api.post<Product>("/products", body),
    update: (id: string, body: ProductUpdateInput) => api.patch<Product>(`/products/${id}`, body),
    duplicate: (id: string) => api.post<Product>(`/products/${id}/duplicate`),
    /** The run "Batch baru" would roll forward: its packages and the form defaults. */
    newBatchPlan: (id: string) => api.get<KelasBatchPlan>(`/products/${id}/new-batch`),
    newBatch: (id: string, body: KelasNewBatchInput) =>
      api.post<KelasNewBatchResult>(`/products/${id}/new-batch`, body),
    /** The WhatsApp groups in a class's info, each with every kelas product using it. */
    whatsappPlan: (id: string) => api.get<KelasWhatsappPlan>(`/products/${id}/whatsapp`),
    changeWhatsapp: (id: string, body: KelasWhatsappInput) =>
      api.post<KelasWhatsappResult>(`/products/${id}/whatsapp`, body),
    kelasBulk: (body: KelasBulkInput) => api.post<KelasBulkResult>("/products/kelas-bulk", body),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/products/${id}`),
    uploadFiles: (files: FormData) => api.upload<DeliveryFile[]>("/products/upload", files),
    uploadImage: async (file: FormData) =>
      api.upload<{ key: string }>("/products/upload-image", await maybeOptimizeImageFormData(file)),
    /** Best-effort R2 cleanup. Pass the product being edited so the server can
     *  discount its own still-unsaved reference when it refcounts the key —
     *  omit it only for a product that doesn't exist server-side yet. */
    deleteFile: (key: string, productId?: string) =>
      api.post<{ deleted: boolean }>("/products/delete-file", { key, productId }),
    /** The seller's own delivery PDF, streamed back so they can check what the
     *  buyer actually downloads. Authorised by the product, not the key prefix. */
    deliveryFile: (key: string) =>
      api.blob(`/products/file?key=${encodeURIComponent(key)}`),
    /** What every active product's own link did over the last LINK_STATS_DAYS days. */
    linkStats: () => api.get<StoreLinkStats>("/products/link-stats"),
    /** Page ids this product is sold on. */
    pages: (id: string) => api.get<string[]>(`/products/${id}/pages`),
    /** Replace which pages sell this product. */
    setPages: (id: string, pageIds: string[]) =>
      api.put<string[]>(`/products/${id}/pages`, { pageIds }),
    /** Saved variation options (0197), also while the switch is off. */
    variation: (id: string) =>
      api.get<{ enabled: boolean; variation: ProductVariation | null }>(`/products/${id}/variation`),
    /** Save the variation; `{ enabled: false }` only switches it off. */
    setVariation: (id: string, body: VariationSave) =>
      api.put<{ enabled: boolean; variation: ProductVariation | null }>(`/products/${id}/variation`, body),
  },

  orders: {
    /** One page. `total` in the reply is the whole result set, not `rows.length`. */
    list: (params: OrdersListParams = {}) =>
      api.get<OrdersListPage>(`/orders${ordersQuery(params)}`),
    /** Counts for the saved-view chips. `withRepeat` costs a scan — ask only when
     *  shown. `gateway` must match the list's filter or the chips contradict it. */
    summary: (withRepeat = false, gateway = "") => {
      const params = new URLSearchParams();
      if (withRepeat) params.set("withRepeat", "1");
      if (gateway.trim()) params.set("gateway", gateway.trim());
      const qs = params.toString();
      return api.get<OrdersSummary>(`/orders/summary${qs ? `?${qs}` : ""}`);
    },
    /** Every row matching the current view + search, for exports only. */
    exportAll: (params: OrdersListParams = {}) =>
      api.get<WithProduct<Order>[]>(`/orders/export${ordersQuery(params, false)}`),
    get: (id: string) => api.get<WithProduct<Order>>(`/orders/${id}`),
    create: (body: OrderCreateInput) => api.post<Order>("/orders", body),
    update: (id: string, body: OrderUpdateInput) =>
      api.patch<WithProduct<Order>>(`/orders/${id}`, body),
    cancel: (id: string) => api.post<WithProduct<Order>>(`/orders/${id}/cancel`),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/orders/${id}`),
    // NinjaVan courier: create a shipment, fetch the waybill PDF, cancel it.
    ship: (id: string) => api.post<WithProduct<Order>>(`/orders/${id}/ship`),
    waybill: (id: string) =>
      api.get<{ pdfBase64: string; filename: string }>(`/orders/${id}/waybill`),
    cancelShipment: (id: string) =>
      api.post<WithProduct<Order>>(`/orders/${id}/cancel-shipment`),
  },

  customers: {
    /** One page. `total` in the reply is the whole result set, not `rows.length`. */
    list: (params: CustomersListParams = {}) =>
      api.get<CustomersListPage>(`/customers${customersQuery(params)}`),
    /** Every row matching the current search, for exports only. */
    exportAll: (params: CustomersListParams = {}) =>
      api.get<CustomerSummary[]>(`/customers/export${customersQuery(params, false)}`),
    get: (email: string) => api.get<CustomerDetail>(`/customers/${encodeURIComponent(email)}`),
    update: (email: string, body: CustomerUpdateInput) =>
      api.patch<SellerCustomer>(`/customers/${encodeURIComponent(email)}`, body),
    suspend: (email: string) =>
      api.post<SellerCustomer>(`/customers/${encodeURIComponent(email)}/suspend`),
    unsuspend: (email: string) =>
      api.post<SellerCustomer>(`/customers/${encodeURIComponent(email)}/unsuspend`),
    assignPackage: (email: string, body: CustomerAssignPackageInput) =>
      api.post<WithProduct<DigitalAccess>>(
        `/customers/${encodeURIComponent(email)}/assign-package`,
        body,
      ),
    changeEmail: (email: string, body: CustomerChangeEmailInput) =>
      api.post<CustomerChangeEmailResult>(
        `/customers/${encodeURIComponent(email)}/change-email`,
        body,
      ),
    resendAccess: (email: string) =>
      api.post<{ sent: boolean; count: number }>(`/customers/${encodeURIComponent(email)}/resend-access`),
    remove: (email: string) =>
      api.delete<{ deleted: boolean }>(`/customers/${encodeURIComponent(email)}`),
  },

  access: {
    list: () => api.get<WithProduct<DigitalAccess>[]>("/digital-access"),
    create: (body: DigitalAccessCreateInput) => api.post<DigitalAccess>("/digital-access", body),
    revoke: (id: string) => api.patch<DigitalAccess>(`/digital-access/${id}/revoke`),
    restore: (id: string) => api.patch<DigitalAccess>(`/digital-access/${id}/restore`),
    regenerate: (id: string) => api.patch<DigitalAccess>(`/digital-access/${id}/regenerate`),
  },

  settings: {
    get: () => api.get<SafeUserSettings>("/settings"),
    update: (body: SettingsUpdateInput) => api.patch<SafeUserSettings>("/settings", body),
    // Verify the store's saved Zoom S2S credentials (save first, then test).
    zoomTest: () => api.post<{ ok: boolean }>("/settings/zoom/test", {}),
    // NinjaVan/Stripe webhook URLs + whether this store still sends to the retired apex.
    webhooks: () => api.get<SellerWebhookInfo>("/settings/webhooks"),
    // Default trainer who signs this store's certificates.
    certificateSigner: () => api.get<CertificateSignerSettings>("/settings/certificate-signer"),
    saveCertificateSigner: (body: CertificateSignerInput) =>
      api.put<CertificateSignerSettings>("/settings/certificate-signer", body),
    // Sent as-is (no image optimizer): it is already a small transparent PNG and
    // re-encoding could drop the alpha the certificate relies on.
    uploadCertificateSignature: (png: Blob) => {
      const fd = new FormData();
      fd.append("file", png, "signature.png");
      return api.upload<{ key: string; data_url: string | null }>("/settings/certificate-signature", fd);
    },
    certificateSignaturePreview: (key: string) =>
      api.get<{ data_url: string | null }>(`/settings/certificate-signature?key=${encodeURIComponent(key)}`),
    // Portals on the BayarCash account a PAT belongs to; omit `pat` to use the stored one.
    bayarcashPortals: (body: BayarcashPortalsInput) =>
      api.post<{ portals: BayarcashPortal[] }>("/settings/bayarcash/portals", body),
    // Sale-notification webhook. The api key is minted server-side (never typed)
    // and revealed on demand so the seller can paste it into their push app.
    notifyKeyCreate: () => api.post<{ api_key: string }>("/settings/notify/key", {}),
    notifyKeyReveal: () => api.get<{ api_key: string }>("/settings/notify/key"),
    // HMAC signing secret. Minting shows it once (paste it into the push app);
    // removing needs its own call because the input is masked, so "empty" on the
    // shared Save means "leave alone", never "delete".
    notifySecretCreate: () => api.post<{ secret: string }>("/settings/notify/secret", {}),
    notifySecretDelete: () => api.delete<{ ok: boolean }>("/settings/notify/secret"),
    // QR enrolment: mints a SHORT-LIVED token (never the credentials — the app
    // redeems it at the PUBLIC /notify/pair for those).
    notifyPairCreate: () =>
      api.post<{ token: string; expires_at: string; redeem_url: string }>(
        "/settings/notify/pair",
        {},
      ),
    // Account-scoped state: which stores one scan will wire, and whether the
    // account is already paired. Answers the same from ANY store, so switching
    // stores never shows a misleading "not paired".
    notifyPairState: () =>
      api.get<{
        paired: boolean;
        webhook_url: string | null;
        stores: { store_id: string; store_slug: string; store_name: string; is_default: boolean }[];
      }>("/settings/notify/pair"),
    // Disconnect EVERY store at once and rotate the shared credentials.
    notifyPairDisconnect: () => api.delete<{ ok: boolean }>("/settings/notify/pair"),
    // Fires a dummy sale at the SAVED url — save before testing.
    notifyTest: () => api.post<{ ok: boolean; status: number | null }>("/settings/notify/test", {}),
  },

  // Tetapan → Integrasi: API keys (REST /api/v1 + MCP). Owner-only server-side.
  integrations: {
    keys: () => api.get<ApiKeySummary[]>("/integrations/keys"),
    createKey: (body: ApiKeyCreateInput) => api.post<ApiKeyCreated>("/integrations/keys", body),
    revokeKey: (id: string) => api.delete<{ revoked: boolean }>(`/integrations/keys/${id}`),
    webhooks: () => api.get<WebhookEndpointSummary[]>("/integrations/webhooks"),
    webhookTargets: () => api.get<WebhookTargets>("/integrations/webhooks/targets"),
    createWebhook: (body: WebhookEndpointCreateInput) =>
      api.post<WebhookEndpointCreated>("/integrations/webhooks", body),
    updateWebhook: (id: string, body: WebhookEndpointUpdateInput) =>
      api.patch<WebhookEndpointSummary>(`/integrations/webhooks/${id}`, body),
    deleteWebhook: (id: string) => api.delete<{ deleted: boolean }>(`/integrations/webhooks/${id}`),
    rotateWebhookSecret: (id: string) =>
      api.post<{ secret: string }>(`/integrations/webhooks/${id}/secret`, {}),
    // Sends a sample event right away (no retries) and reports what came back.
    testWebhook: (id: string, event?: WebhookEvent) =>
      api.post<WebhookTestResult>(`/integrations/webhooks/${id}/test`, event ? { event } : {}),
    deliveries: (id: string) =>
      api.get<WebhookDeliverySummary[]>(`/integrations/webhooks/${id}/deliveries`),
    redeliver: (deliveryId: string) =>
      api.post<WebhookTestResult>(`/integrations/deliveries/${deliveryId}/redeliver`, {}),
    // OAuth consent page (claude.ai / ChatGPT connectors). `query` is the
    // authorization request's own query string, passed through untouched.
    // Every change apps made through a key or OAuth connection, newest first.
    audit: () => api.get<ApiAuditEntry[]>("/integrations/audit?limit=100"),
    oauthCheck: (query: string) =>
      api.get<OAuthAuthorizeCheck>(`/oauth/authorize/check${query}`, { public: true }),
    oauthDecide: (body: Record<string, unknown>) =>
      api.post<{ redirect_to: string }>("/oauth/authorize", body),
  },

  // Affiliate program — the seller's per-store config (enable + rate + who may earn).
  affiliates: {
    getSettings: () => api.get<SellerAffiliateSettings>("/affiliates/settings"),
    updateSettings: (body: AffiliateSettingsInput) =>
      api.put<SellerAffiliateSettings>("/affiliates/settings", body),
    // Seller's management view of affiliates promoting their active store.
    manage: () => api.get<SellerAffiliateDashboard>("/affiliates/manage"),
    switchProgram: (on: boolean) => api.post<SellerAffiliateSettings>("/affiliates/program/switch", { on }),
    // The caller's own affiliate account + earnings (self-serve).
    me: () => api.get<AffiliateMe>("/affiliates/me"),
    // Joining requires agreeing to the programme terms; the server refuses
    // anything else, so the flag is not optional on this side either.
    activate: () => api.post<Affiliate>("/affiliates/me/activate", { acceptTerms: true }),
    signup: () => api.post<Affiliate>("/affiliates/me/signup", { acceptTerms: true }),
    setPayout: (body: AffiliatePayoutInput) =>
      api.put<Affiliate>("/affiliates/me/payout", body),
    earnings: () => api.get<AffiliateEarnings>("/affiliates/me/earnings"),
    // Coupon attribution + per-product rates (seller side, active store).
    listCoupons: () => api.get<{ coupons: AffiliateCouponRow[] }>("/affiliates/coupons"),
    bindCoupon: (body: AffiliateCouponInput) =>
      api.put<{ bound: boolean }>("/affiliates/coupons", body),
    productSettings: () =>
      api.get<AffiliateProductSettings>("/affiliates/product-settings"),
    saveProductSetting: (body: AffiliateProductSettingInput) =>
      api.put<{ saved: boolean }>("/affiliates/product-settings", body),
  },

  /** Seller-side management of the active store's affiliate programme. */
  affiliateManage: {
    decideMembership: (membershipId: string, action: "approve" | "reject") =>
      api.post<{ status: "active" | "rejected" }>(
        `/affiliate-manage/roster/${membershipId}/decision`,
        { action },
      ),
    setMembershipStatus: (membershipId: string, status: "active" | "suspended") =>
      api.post<{ status: "active" | "suspended" }>(`/affiliate-manage/roster/${membershipId}/status`, { status }),
    nudges: () => api.get<AffiliateProgramNudges>("/affiliate-manage/nudges"),
    referralBulk: (ids: string[], action: "approve" | "reject") =>
      api.post<{ done: number; failed: Array<{ id: string; error: string }> }>("/affiliate-manage/referrals/bulk", { ids, action }),
    rosterDetail: (affiliateId: string) => api.get<import("@shared/types").AffiliateRosterDetail>(`/affiliate-manage/roster/${affiliateId}/detail`),
    reportBreakdown: (julat: string) =>
      api.get<import("@shared/types").AffiliateReportBreakdown>(`/affiliate-manage/report-breakdown?${julat}`),
    performance: (julat: string) => api.get<import("@shared/types").AffiliatePerformance>(`/affiliate-manage/performance?${julat}`),
    invites: () => api.get<import("@shared/types").AffiliateInviteRow[]>("/affiliate-manage/invites"),
    createInvite: (label: string) => api.post<import("@shared/types").AffiliateInviteRow>("/affiliate-manage/invites", { label }),
    revokeInvite: (id: string) => api.post<{ ok: true }>(`/affiliate-manage/invites/${id}/revoke`, {}),
    renewInvite: (id: string) => api.post<import("@shared/types").AffiliateInviteRow>(`/affiliate-manage/invites/${id}/renew`, {}),
    referrals: (status?: string) =>
      api.get<{ referrals: AffiliateReferralRow[]; count: number }>(
        `/affiliate-manage/referrals${status ? `?status=${status}` : ""}`,
      ),
    referralAction: (id: string, body: AffiliateReferralActionInput) =>
      api.post<{ id: string }>(`/affiliate-manage/referrals/${id}/action`, body),
    payouts: (status?: string) =>
      api.get<{ payouts: AffiliatePayoutRow[]; count: number }>(
        `/affiliate-manage/payouts${status ? `?status=${status}` : ""}`,
      ),
    owed: () =>
      api.get<{ owed: AffiliateOwedRow[]; bank_url: string; store_name: string }>("/affiliate-manage/owed"),
    remindBank: (affiliateId: string, body: AffiliateBankReminderInput) =>
      api.post<{
        channel: "email" | "whatsapp";
        sent: boolean;
        at: string;
        next_email_at: string | null;
      }>(`/affiliate-manage/owed/${affiliateId}/remind`, body),
    remindAllBank: () =>
      api.post<{ sent: number; failed: number; remaining: number }>("/affiliate-manage/owed/remind-all", {}),
    generatePayout: (body: AffiliatePayoutGenerateInput) =>
      api.post<{ payout_id: string; amount_cents: number; referrals: number }>(
        "/affiliate-manage/payouts/generate",
        body,
      ),
    setPayoutStatus: (id: string, body: AffiliatePayoutStatusInput) =>
      api.post<{ id: string; status: string }>(`/affiliate-manage/payouts/${id}/status`, body),
    // Audited on every call — the full number is only for making the transfer.
    revealBank: (id: string) =>
      api.get<{ bank_name: string | null; account_holder: string | null; account_number: string }>(
        `/affiliate-manage/payouts/${id}/bank`,
      ),
    assets: () => api.get<{ assets: AffiliateAsset[] }>("/affiliate-manage/assets"),
    saveAsset: (body: AffiliateAssetInput) =>
      api.put<{ id: string }>("/affiliate-manage/assets", body),
    deleteAsset: (id: string) =>
      api.delete<{ deleted: boolean }>(`/affiliate-manage/assets/${id}`),
    templates: () =>
      api.get<{
        templates: { event: string; enabled: boolean; subject: string; body: string }[];
      }>("/affiliate-manage/templates"),
    saveTemplate: (body: AffiliateTemplateInput) =>
      api.put<{ saved: boolean }>("/affiliate-manage/templates", body),
    testTemplate: (event: string) =>
      api.post<{ sent: boolean }>("/affiliate-manage/templates/test", { event }),
    /** Over the page's date range as a query (121L): every bucket, and the span before's totals. */
    report: (julat: string) =>
      api.get<{
        points: AffiliateReportPoint[];
        julat: { from: string; to: string; prevFrom: string | null; prevTo: string | null; bucket: "hour" | "day" | "month"; earliest: string | null };
        totals: AffiliateReportTotals;
        prevTotals: AffiliateReportTotals | null;
      }>(`/affiliate-manage/report?${julat}`),
  },

  /** The affiliate's own portal — always scoped to the caller, never to an id. */
  affiliatePortal: {
    summary: () => api.get<AffiliatePortalSummary>("/affiliate-portal/summary"),
    ledger: (params: { store?: string; status?: string } = {}) => {
      const q = new URLSearchParams();
      if (params.store) q.set("store", params.store);
      if (params.status) q.set("status", params.status);
      const qs = q.toString();
      return api.get<AffiliateLedgerPage>(`/affiliate-portal/ledger${qs ? `?${qs}` : ""}`);
    },
    links: () =>
      api.get<{
        cards: AffiliateLinkCard[];
        pages: AffiliatePageCard[];
        offers: AffiliateStoreOffer[];
      }>("/affiliate-portal/links"),
    /** `iklan`: the Iklan HQ ad the affiliate joined from (charged per join), with its slot. */
    joinStore: (storeId: string, from?: { iklan: string; s?: IklanSlot }) =>
      api.post<{ join_state: AffiliateJoinState }>(
        `/affiliate-portal/stores/${storeId}/join`,
        from ?? {},
      ),
    traffic: () =>
      api.get<{ rows: AffiliateTrafficRow[]; count: number }>("/affiliate-portal/traffic"),
    getBank: () => api.get<AffiliateBankAccount | null>("/affiliate-portal/bank"),
    getContact: () => api.get<{ whatsapp: string | null }>("/affiliate-portal/contact"),
    completion: () => api.get<AffiliateCompletion>("/affiliate-portal/completion"),
    saveContact: (body: AffiliateContactInput) =>
      api.put<{ whatsapp: string | null }>("/affiliate-portal/contact", body),
    saveBank: (body: AffiliateBankInput) =>
      api.put<{ saved: boolean }>("/affiliate-portal/bank", body),
    terms: () =>
      api.get<{
        terms: { store_id: string; terms_html: string; terms_version: string }[];
        accepted_at: string | null;
        accepted_version: string | null;
      }>("/affiliate-portal/terms"),
    acceptTerms: (version: string) =>
      api.post<{ accepted: boolean }>("/affiliate-portal/terms/accept", { version }),
    leaderboard: () =>
      api.get<{
        rows: { rank: number; code: string; display_name: string | null; earned_cents: number }[];
      }>("/affiliate-portal/leaderboard"),
    setLeaderboardOptIn: (optIn: boolean) =>
      api.put<{ opt_in: boolean }>("/affiliate-portal/leaderboard-opt-in", { optIn }),
    portalAssets: () => api.get<{ assets: AffiliateAsset[] }>("/affiliate-portal/assets"),
    pixel: () =>
      api.get<{
        pixel: {
          enabled: boolean;
          pixel_id: string | null;
          server_events_enabled: boolean;
          access_token_set: boolean;
          test_event_code: string | null;
        };
        /** How far the one pixel reaches, since there is no longer a list. */
        scope: { stores: number; products: number };
      }>("/affiliate-portal/pixel"),
    savePixel: (body: AffiliatePixelInput) =>
      api.put<{ saved: boolean }>("/affiliate-portal/pixel", body),
    pixelLogs: () =>
      api.get<{
        logs: {
          id: string;
          order_id: string;
          platform: string;
          event_name: string;
          method: string;
          amount_cents: number | null;
          currency: string | null;
          success: boolean;
          error: string | null;
          created_at: string;
        }[];
      }>("/affiliate-portal/pixel-logs"),
    requestPayout: (body: AffiliatePayoutRequestInput) =>
      api.post<{ payout_id: string; amount_cents: number }>(
        "/affiliate-portal/payout-request",
        body,
      ),
  },

  // Stores (multi-store). The dashboard currently drives only the default store;
  // `list` surfaces it (and the plan's store cap) and `update` patches it.
  stores: {
    list: () =>
      api.get<{
        stores: MyStore[];
        limit: number | null;
        used: number;
        canAdd: boolean;
        allowCustomDomain: boolean;
      }>("/stores"),
    update: (id: string, body: StoreUpdateInput) =>
      api.patch<MyStore>(`/stores/${id}`, body),
    // Tetapan › Homepage: can the root be the store's FYP, and is it.
    fypHome: (id: string) => api.get<FypHomeState>(`/stores/${id}/fyp-home`),
    // Upload one image (≤5MB) for a bio-web image block. Returns the R2 key to
    // store on the block; the public storefront streams it by store+block id.
    uploadImage: async (id: string, file: FormData) =>
      api.upload<{ key: string }>(`/stores/${id}/upload-image`, await maybeOptimizeImageFormData(file)),
    // Custom domain (Premium): attach (registers at the edge, returns DNS
    // records), re-poll verification status, or detach. attach/verify also report
    // `dnsAutoAdded` when the records were written automatically (same-account zone).
    attachDomain: (id: string, domain: string) =>
      api.post<Store & { dnsAutoAdded?: boolean }>(`/stores/${id}/custom-domain`, { domain }),
    verifyDomain: (id: string) =>
      api.post<Store & { dnsAutoAdded?: boolean }>(`/stores/${id}/custom-domain/verify`, undefined),
    removeDomain: (id: string) => api.delete<Store>(`/stores/${id}/custom-domain`),
    // Permanently delete an ADDED store and all its data. Requires the exact
    // store name retyped + the account password (server re-verifies both).
    destroy: (id: string, body: { confirmName: string; password: string }) =>
      api.post<{ deleted: true }>(`/stores/${id}/destroy`, body),
  },

  // Seller subscription plans.
  plans: {
    /** Public list of active tiers (pricing page). */
    listPublic: () => api.get<PlanConfig[]>("/plans", { public: true }),
    /** Public: which gateways a subscriber may pick right now. */
    checkoutOptions: () =>
      api.get<SubscriptionCheckoutOptions>("/plans/checkout-options", { public: true }),
    /** The caller's current tier + billing history. */
    me: () => api.get<MyPlanWithHistory>("/plans/me"),
    /** Products/pages/stores used, for the cap meters. Its own call so the
     *  dashboard gate's /plans/me stays a single cheap read. */
    usage: () => api.get<PlanUsage>("/plans/usage"),
    /** Start a purchase on a chosen gateway — returns the payment URL. */
    subscribe: (body: SubscribeInput) =>
      api.post<StartSubscriptionResult>("/payments/subscription/create-bill", body),
    /** Public "Langgan terus": create account + open payment in one step. */
    publicSubscribe: (body: PublicSubscribeInput) =>
      api.post<StartSubscriptionResult>("/payments/subscription/subscribe", body, { public: true }),
    /** Public "Langgan terus" via an Akses Kod: create account + activate instantly, no payment. */
    publicSubscribeWithCode: (body: PublicSubscribeCodeInput) =>
      api.post<{
        // null for a gate-only (Trial) code — the seller lands on the dashboard.
        subscriptionId: string | null;
        activated: boolean;
        session: { access_token: string; refresh_token: string } | null;
      }>("/payments/subscription/subscribe-with-code", body, { public: true }),
    /** Poll a subscription's status (activates the plan if paid). */
    subscriptionStatus: (id: string) =>
      api.get<Pick<SubscriptionPayment, "id" | "plan" | "months" | "amount_cents" | "currency" | "status" | "period_end">>(
        `/payments/subscription/status/${id}`,
      ),
    /** Public poll (unguessable id) for the "Langgan terus" return page. */
    publicSubscriptionStatus: (id: string) =>
      api.get<Pick<SubscriptionPayment, "status" | "plan" | "amount_cents" | "currency">>(
        `/payments/subscription/public-status/${id}`,
        { public: true },
      ),
    /** Redeem a coupon (multipart: code + receipt file) — lands as awaiting review. */
    redeemCoupon: (form: FormData) =>
      api.upload<Pick<SubscriptionPayment, "id" | "plan" | "months" | "status">>(
        "/payments/subscription/redeem-coupon",
        form,
      ),
  },

  /** Ask the storefront whether a dead path has a redirect rule. Called ONLY
   *  after a page lookup already 404'd, so the happy path pays nothing. `null`
   *  means the path is genuinely gone — render the not-found card. */
  resolveRedirect: (params: { path: string; query: string; sub: string; domain: string }) =>
    api.get<RedirectResolution | null>(
      `/public/redirect?${new URLSearchParams(params).toString()}`,
      { public: true },
    ),

  // Public project menu (/projek) — hidden, access-code-only plan buckets.
  projects: {
    /** Active projects that have at least one active tier. */
    list: () => api.get<PlanProject[]>("/projects", { public: true }),
    /** One project + its active tiers. */
    get: (slug: string) =>
      api.get<{ project: PlanProject; tiers: PlanConfig[] }>(`/projects/${slug}`, {
        public: true,
      }),
  },

  discounts: {
    list: () => api.get<DiscountCode[]>("/discounts"),
    create: (body: DiscountCodeCreateInput) => api.post<DiscountCode>("/discounts", body),
    update: (id: string, body: DiscountCodeUpdateInput) =>
      api.patch<DiscountCode>(`/discounts/${id}`, body),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/discounts/${id}`),
  },

  // Redirect rules for dead storefront URLs (Settings -> Pengalihan).
  redirects: {
    list: () => api.get<StoreRedirect[]>("/redirects"),
    create: (body: RedirectCreateInput) => api.post<StoreRedirect>("/redirects", body),
    update: (id: string, body: RedirectUpdateInput) =>
      api.patch<StoreRedirect>(`/redirects/${id}`, body),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/redirects/${id}`),
  },

  // Settings → Domain → Domain landing (125E, superadmin only for now).
  landingDomains: {
    view: () => api.get<LandingDomainsView>("/landing-domains"),
    add: (body: LandingDomainCreateInput) => api.post<LandingDomainsAddResult>("/landing-domains", body),
    update: (id: string, body: LandingDomainUpdateInput) =>
      api.patch<LandingDomain>(`/landing-domains/${id}`, body),
    verify: (id: string) => api.post<LandingDomain>(`/landing-domains/${id}/verify`, {}),
    remove: (id: string) => api.delete<{ deleted: boolean }>(`/landing-domains/${id}`),
  },

  // Seller support tickets (bug / feature / feedback chat with the operator).
  supportAi: {
    ask: (body: import("@shared/supportAi").SupportAskInput) =>
      api.post<import("@shared/supportAi").SupportAnswer>("/support-ai/ask", body, { timeoutMs: 60_000 }),
    vote: (id: string, vote: 1 | -1) => api.post<{ ok: true }>(`/support-ai/turns/${encodeURIComponent(id)}/vote`, { vote }),
    scan: () => api.get<import("@shared/storeScan").StoreScan>("/support-ai/scan", { timeoutMs: 30_000 }),
    buyers: (q: string) => api.get<import("@shared/buyerDiagnosis").BuyerMatch[]>(`/support-ai/buyers?q=${encodeURIComponent(q)}`, { timeoutMs: 30_000 }),
    undelivered: () => api.get<import("@shared/buyerDiagnosis").BuyerMatch[]>("/support-ai/buyers/undelivered", { timeoutMs: 30_000 }),
    credits: () => api.get<import("@shared/buatkan").BuatkanCredits>("/support-ai/credits"),
    buatkanPlan: (body: import("@shared/buatkan").BuatkanPlanInput) =>
      api.post<import("@shared/buatkan").BuatkanPlan>("/support-ai/buatkan/plan", body, { timeoutMs: 60_000 }),
    buatkanOutcome: (id: string, outcome: "done" | "failed" | "undone" | "cancelled") =>
      api.post<{ ok: true }>(`/support-ai/buatkan/${encodeURIComponent(id)}/outcome`, { outcome }),
    creditContact: () => api.get<import("@shared/buatkan").TopupContact>("/support-ai/credits/contact"),
    buyCredits: (body: { pack: import("@shared/buatkan").CreditPackId; returnPath: string; phone?: string; name?: string; email?: string }) =>
      api.post<{ id: string; paymentUrl: string }>("/support-ai/credits/buy", body, { timeoutMs: 30_000 }),
    openTopup: () => api.get<import("@shared/buatkan").TopupResume>("/support-ai/credits/open-topup", { timeoutMs: 30_000 }),
    cancelTopup: (id: string) => api.post<{ ok: true }>(`/support-ai/credits/topups/${encodeURIComponent(id)}/cancel`, {}),
    handoff: (body: import("@shared/supportAi").HandoffInput) => api.post<import("@shared/supportAi").Handoff>("/support-ai/handoff", body, { timeoutMs: 60_000 }),
    topup: (id: string) => api.get<import("@shared/buatkan").CreditTopup>(`/support-ai/credits/topups/${encodeURIComponent(id)}`, { timeoutMs: 30_000 }),
  },

  support: {
    list: () => api.get<SupportTicket[]>("/support"),
    get: (id: string) => api.get<SupportTicketDetail>(`/support/${id}`),
    create: (body: SupportTicketCreateInput) => api.post<SupportTicketDetail>("/support", body),
    reply: (id: string, body: SupportMessageInput) =>
      api.post<SupportMessage>(`/support/${id}/messages`, body),
    uploadImage: async (file: FormData) =>
      api.upload<{ key: string }>("/support/upload-image", await maybeOptimizeImageFormData(file)),
    attachment: (ticketId: string, messageId: string) =>
      api.blob(`/support/${ticketId}/messages/${messageId}/attachment`),
    deskTicket: () => api.get<{ ticket: string; name?: string }>("/support/desk-ticket"),
  },

  publicPage: (slug: string) => api.get<PublicPage>(`/public/pages/${slug}`, { public: true }),
  // Recent-order social-proof feed for the checkout toast. Host-agnostic: pass
  // whatever the storefront resolved by (apex slug/handle, subdomain, or domain);
  // the server scopes to that store and returns [] unless the seller opted in.
  publicRecentOrders: (q: { slug?: string; handle?: string; sub?: string; domain?: string }) => {
    const p = new URLSearchParams();
    if (q.slug) p.set("slug", q.slug);
    if (q.handle) p.set("handle", q.handle);
    if (q.sub) p.set("sub", q.sub);
    if (q.domain) p.set("domain", q.domain);
    const path = `/public/recent-orders?${p.toString()}`;
    // One-page layouts mount the toast AND the checkout panel: one request serves both.
    const hit = recentOrdersMemo.get(path);
    if (hit && Date.now() - hit.at < RECENT_ORDERS_MEMO_MS) return hit.promise;
    const promise = api.get<{ orders: PublicRecentOrder[] }>(path, { public: true });
    recentOrdersMemo.set(path, { at: Date.now(), promise });
    promise.catch(() => recentOrdersMemo.delete(path));
    return promise;
  },
  publicPageByHandle: (handle: string, slug: string) =>
    api.get<PublicPage>(`/public/h/${handle}/${slug}`, { public: true }),
  // Store-subdomain renderers: pages scoped to the store owning <subdomain>.
  publicPageBySubdomain: (sub: string, slug: string) =>
    api.get<PublicPage>(`/public/s/${sub}/${slug}`, { public: true }),
  publicPageBySubHandle: (sub: string, handle: string, slug: string) =>
    api.get<PublicPage>(`/public/s/${sub}/${handle}/${slug}`, { public: true }),
  // Same path builder as index.html's boot script, which may already have this in flight.
  publicStore: (sub: string, opts: { bioOnly?: boolean } = {}) =>
    api.get<PublicStore>(storeLandingPath({ kind: "subdomain", subdomain: sub }, opts.bioOnly), { public: true }),
  publicSubdomainCheck: (sub: string) =>
    api.get<SubdomainCheck>(`/public/subdomain/${encodeURIComponent(sub)}`, { public: true }),
  // Custom-domain storefront renderers: pages scoped to the store that owns the
  // verified domain <host>.
  publicPageByDomain: (host: string, slug: string) =>
    api.get<PublicPage>(`/public/d/${encodeURIComponent(host)}/pages/${slug}`, { public: true }),
  publicPageByDomainHandle: (host: string, handle: string, slug: string) =>
    api.get<PublicPage>(`/public/d/${encodeURIComponent(host)}/h/${handle}/${slug}`, {
      public: true,
    }),
  publicStoreByDomain: (host: string, opts: { bioOnly?: boolean } = {}) =>
    api.get<PublicStore>(storeLandingPath({ kind: "domain", host }, opts.bioOnly), { public: true }),
  // The reseller plan-signup page — a surface of its own at `<host>/plan`, so the
  // storefront root stays the seller's product shop. 404 on an ordinary store.
  publicResellerPlan: (sub: string) =>
    api.get<PublicResellerPlan>(`/public/s/${encodeURIComponent(sub)}/plan`, { public: true }),
  publicResellerPlanByDomain: (host: string) =>
    api.get<PublicResellerPlan>(`/public/d/${encodeURIComponent(host)}/plan`, { public: true }),
  // Single-product checkout scoped to a storefront host (subdomain / custom domain).
  publicStoreProductBySubdomain: (sub: string, slug: string) =>
    api.get<ShopStore>(`/public/s/${sub}/product/${slug}`, { public: true }),
  publicStoreProductByDomain: (host: string, slug: string) =>
    api.get<ShopStore>(`/public/d/${encodeURIComponent(host)}/product/${slug}`, { public: true }),
  // Lazily-loaded sales page embed for a product (fetched on "Lihat Selengkapnya").
  publicProductSalesPage: (productId: string) =>
    api.get<ProductSalesPage | null>(`/public/products/${productId}/sales-page`, { public: true }),
  // Public testimonials (average, count, newest) for a product's Shopee-style
  // reviews. `pageId` folds in the sales page's pool (0139) — testimonials
  // earned through this page even when the product they were about is no
  // longer linked to it (and only when the page owner opted in).
  publicProductReviews: (productId: string, pageId?: string | null) =>
    api.get<PublicProductReviews>(
      `/public/products/${productId}/reviews${pageId ? `?pageId=${encodeURIComponent(pageId)}` : ""}`,
      { public: true },
    ),
  // Buyer's own access content — behind login; the server checks ownership so a
  // leaked/shared token can't unlock it (unlike the old public endpoint).
  accountAccess: (token: string) => api.get<PublicAccess>(`/account/access/${token}`),
  /** Join a later run of a kelas with a purchase that includes more than one class. */
  joinKelasBatch: (token: string, productId: string) =>
    api.post<{ access_token: string; access_url: string }>(`/account/access/${token}/join-batch`, { productId }),
  /** Bring someone to the class on one of the purchase's seats. */
  inviteKelasGuest: (token: string, body: { name: string; email: string }) =>
    api.post<KelasGuests>(`/account/access/${token}/guests`, body),
  removeKelasGuest: (token: string, email: string) =>
    api.post<KelasGuests>(`/account/access/${token}/guests/remove`, { email }),
  // Mint a magic-login link that carries the buyer's session onto the apex,
  // landing on the purchases hub — the thank-you page's "go to my dashboard"
  // button, which must not dump a just-paid buyer on a login page. Opening a
  // single product needs no such bridge; it stays same-origin.
  accountHubHandoff: () => api.post<{ loginUrl: string }>("/account/handoff"),
  publicCreateBill: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/toyyibpay/create-bill", body, { public: true }),
  publicCreateStripeSession: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/stripe/create-session", body, { public: true }),
  publicCreateBayarcashIntent: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/bayarcash/create-intent", body, { public: true }),
  publicCreateChipPurchase: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/chip/create-purchase", body, { public: true }),
  publicCreateAtomePayment: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/atome/create-payment", body, { public: true }),
  publicCreateBillplzBill: (body: CreateBillInput) =>
    api.post<CreateBillResult>("/payments/billplz/create-bill", body, { public: true }),
  publicCompleteFreeOrder: (body: CreateBillInput) =>
    api.post<{ orderId: string; paymentUrl: string }>("/payments/free/complete", body, {
      public: true,
    }),
  // Cash-on-Delivery: create an unpaid physical order settled on delivery.
  publicCreateCodOrder: (body: CreateBillInput) =>
    api.post<{ orderId: string; paymentUrl: string }>("/payments/cod/create", body, {
      public: true,
    }),
  publicDiscountPreview: (body: DiscountPreviewInput) =>
    api.post<DiscountPreview>("/payments/discount/preview", body, { public: true }),
  publicAdCouponPreview: (body: AdCouponPreviewInput) =>
    api.post<AdCouponPreview>("/payments/discount/ad-preview", body, { public: true }),
  publicResendAccess: (email: string, turnstileToken?: string) =>
    api.post<{ sent: boolean }>(
      "/public/access/resend",
      { email, turnstileToken },
      { public: true },
    ),
  publicOrderStatus: (orderId: string) =>
    api.get<PublicOrderStatus>(`/public/orders/${orderId}/status`, { public: true }),
  publicEnsureAccess: (orderId: string) =>
    api.post<EnsureAccessResult>(`/public/orders/${orderId}/ensure-access`, undefined, {
      public: true,
    }),
  publicClaimAccount: (orderId: string) =>
    api.post<ClaimAccountResult>(`/public/orders/${orderId}/claim-account`, undefined, {
      public: true,
    }),
  publicRetryOrder: (orderId: string) =>
    api.post<CreateBillResult>(`/payments/toyyibpay/retry/${orderId}`, undefined, {
      public: true,
    }),
  publicRetryBayarcashOrder: (orderId: string) =>
    api.post<CreateBillResult>(`/payments/bayarcash/retry/${orderId}`, undefined, {
      public: true,
    }),
  publicRetryChipOrder: (orderId: string) =>
    api.post<CreateBillResult>(`/payments/chip/retry/${orderId}`, undefined, {
      public: true,
    }),
  publicRetryAtomeOrder: (orderId: string) =>
    api.post<CreateBillResult>(`/payments/atome/retry/${orderId}`, undefined, {
      public: true,
    }),
  publicRetryBillplzOrder: (orderId: string) =>
    api.post<CreateBillResult>(`/payments/billplz/retry/${orderId}`, undefined, {
      public: true,
    }),
  /** Hand Billplz's signed redirect params to the server so a verified return
   *  settles at once instead of waiting out the poll throttle. */
  publicBillplzReturn: (orderId: string, params: Record<string, string>) =>
    api.post<{ status: string }>(`/payments/billplz/return/${orderId}`, { params }, {
      public: true,
    }),
  publicShop: (storeSlug: string) =>
    api.get<ShopStore>(`/public/shop/${storeSlug}`, { public: true }),
  /** An Iklan HQ offer's product, as its own store sells it (HQ-origin sheet). */
  publicIklanOffer: (offerId: string) =>
    api.get<ShopStore>(`/public/iklan-offer/${encodeURIComponent(offerId)}`, { public: true }),
  /** Cross-seller marketplace ("Jelajah"): products or pages across all opted-in stores. */
  marketplace: (params: {
    tab: "products" | "pages";
    sort?: MarketplaceSort;
    q?: string;
    /** product_type values to keep; empty/absent = every type. */
    types?: string[];
    minPrice?: number;
    maxPrice?: number;
    limit?: number;
    offset?: number;
  }) => {
    const qs = new URLSearchParams({ tab: params.tab });
    if (params.sort) qs.set("sort", params.sort);
    if (params.q) qs.set("q", params.q);
    if (params.types?.length) qs.set("types", params.types.join(","));
    if (params.minPrice) qs.set("min", String(params.minPrice));
    if (params.maxPrice) qs.set("max", String(params.maxPrice));
    if (params.limit != null) qs.set("limit", String(params.limit));
    if (params.offset != null) qs.set("offset", String(params.offset));
    return api.get<MarketplaceResult<MarketplaceProduct | MarketplacePageItem>>(
      `/public/marketplace?${qs.toString()}`,
      { public: true },
    );
  },
  publicTrack: (body: TrackEventInput) =>
    api.post<{ tracked: boolean }>("/public/track", body, { public: true }),
  publicTrackProduct: (body: TrackProductViewInput) =>
    api.post<{ tracked: boolean }>("/public/track-product", body, { public: true }),
  // Abandoned-checkout capture: fire on valid email (before payment). The server
  // no-ops unless the seller opted the store into recovery.
  publicTrackAbandoned: (body: TrackAbandonedInput) =>
    api.post<{ tracked: boolean; id?: string | null }>("/public/cart-recovery/track", body, { public: true }),
  // Contact prefill for a recovery resume link (?resume=<id>).
  publicCartRecoveryResume: (id: string) =>
    api.get<CartRecoveryResume>(`/public/cart-recovery/${id}`, { public: true }),
  /** Platform-wide Meta Pixel id for the apex acquisition funnel (null when off). */
  supportDesk: () => api.get<import("@shared/supportDesk").PublicDesk>("/support-desk/status", { public: true }),
  /** Bantuan, the buyers' chat (117U S10). Public: the chat's signed token is its authority. */
  /** Sokongan pembeli (118I S6): the buyers' chats Bantuan handed to the active store. */
  sellerSupport: {
    count: () => api.get<{ total: number; pending: number }>("/seller-support/count"),
    threads: () => api.get<import("@shared/sellerSupport").SellerChatRow[]>("/seller-support/threads"),
    thread: (id: string) => api.get<import("@shared/sellerSupport").SellerChatThread>(`/seller-support/threads/${encodeURIComponent(id)}`),
    reply: (id: string, body: string) => api.post<import("@shared/sellerSupport").SellerChatThread>(`/seller-support/threads/${encodeURIComponent(id)}/messages`, { body }),
    giveBack: (id: string, reason: string) => api.post<{ ok: true }>(`/seller-support/threads/${encodeURIComponent(id)}/return`, { reason }),
    resolve: (id: string) => api.post<{ ok: true }>(`/seller-support/threads/${encodeURIComponent(id)}/resolve`, {}),
    deskTicket: () => api.get<{ ticket: string }>("/seller-support/desk-ticket"),
    ai: () => api.get<{ on: boolean; balance: number }>("/seller-support/ai"),
    setAi: (on: boolean) => api.put<{ on: boolean; balance: number }>("/seller-support/ai", { on }),
  },
  bantuan: {
    config: (where: { store?: string | null; sub?: string; domain?: string; surface?: string } = {}) => {
      const q = new URLSearchParams();
      if (where.store) q.set("store", where.store);
      else if (where.sub) q.set("sub", where.sub);
      else if (where.domain) q.set("domain", where.domain);
      if (where.surface) q.set("surface", where.surface);
      return api.get<{ enabled: boolean; name: string | null; storeId: string | null; desk: import("@shared/supportDesk").PublicDesk | null }>(`/bantuan/config${q.toString() ? `?${q}` : ""}`, { public: true });
    },
    ask: (body: { question: string; token?: string; storeId?: string; surface: string; history?: { role: "buyer" | "bot"; text: string }[]; storeHint?: string; lang?: "bm" | "en"; page?: string; retry?: boolean; image?: { mediaType: "image/jpeg"; data: string } }) =>
      api.post<{ mode: "bot" | "staff" | "seller"; answer: import("@shared/supportBot").BotAnswerView | null; handedTo?: string | null; token?: string; threadId: string }>("/bantuan/ask", body, { public: true, timeoutMs: 90_000 }),
    code: (body: { email: string; token?: string; storeId?: string; turnstileToken?: string }) => api.post<{ sent: true }>("/bantuan/code", body, { public: true }),
    contact: (body: import("@shared/bantuanContact").ContactInput) => api.post<{ name: string; email: string }>("/bantuan/contact", body, { public: true }),
    sendAccess: (body: { email: string; token?: string; storeId?: string; turnstileToken?: string }) => api.post<{ sent: true }>("/bantuan/send-access", body, { public: true }),
    verify: (body: { email: string; code: string; token?: string }) => api.post<{ token: string; email: string }>("/bantuan/verify", body, { public: true }),
    action: (body: { token: string; action: "resend_access" | "resume_payment"; orderRef?: string; orderId?: string; storeId?: string }) =>
      api.post<{ sent?: boolean; count?: number; url?: string }>("/bantuan/action", body, { public: true, timeoutMs: 30_000 }),
    vote: (body: { token: string; turnId: string; vote: 1 | -1 }) => api.post<{ ok: true }>("/bantuan/vote", body, { public: true }),
    staff: (token: string) => api.post<{ desk: import("@shared/supportDesk").PublicDesk }>("/bantuan/staff", { token }, { public: true }),
    unresolved: (token: string) => api.post<{ result: "escalated" | "reminded" }>("/bantuan/unresolved", { token }, { public: true }),
    thread: (token: string) => api.get<{ messages: import("@shared/types").SupportMessage[]; withStaff: boolean; seller?: string | null }>(`/bantuan/thread?token=${encodeURIComponent(token)}`, { public: true }),
    deskTicket: (token: string) => api.get<{ ticket: string }>(`/bantuan/desk-ticket?token=${encodeURIComponent(token)}`, { public: true }),
    /** /account only: the signed-in buyer's chat, purchases card and stores. */
    account: (token?: string | null) => api.get<import("@shared/bantuanAccount").AccountChat>(`/bantuan/account${token ? `?token=${encodeURIComponent(token)}` : ""}`),
    accountEmailCode: (newEmail: string) => api.post<{ sent: true }>("/bantuan/account/email-code", { newEmail }),
    accountEmail: (body: { newEmail: string; code: string; token?: string }) =>
      api.post<{ email: string; stores: number; needsStaff: boolean; token: string }>("/bantuan/account/email", body, { timeoutMs: 45_000 }),
    emailFix: (body: { token: string; phoneLast4: string; typedEmail?: string; storeId?: string }) =>
      api.post<{ result: "done" | "pending" | "miss" | "staff"; triesLeft: number; fix: import("@shared/emailFix").EmailFixView | null }>("/bantuan/email-fix", body, { public: true, timeoutMs: 45_000 }),
  },
  platformPixel: () =>
    api.get<{ pixelId: string | null }>("/public/platform-pixel", { public: true }),
  /** Which page the apex root ("/") renders — set by the super-admin. A "page"
   *  choice inlines the whole landing-library page so the root needs no 2nd fetch. */
  homepageChoice: () =>
    api.get<{ homepage: HomepageChoice; page: PublicPage | null }>("/public/homepage", {
      public: true,
    }),

  ebooks: {
    list: () => api.get<{ slug: string; title: string; url: string }[]>("/ebooks"),
  },
  account: {
    access: () => api.get<AccountAccess[]>("/account/access"),
    ebook: (slug: string) => api.get<{ url: string }>(`/account/ebook/${encodeURIComponent(slug)}`),
    courses: () => api.get<CourseCard[]>("/account/courses"),
    orders: () => api.get<AccountOrder[]>("/account/orders"),
    certificates: () => api.get<AccountCertificate[]>("/account/certificates"),
    hubSummary: () => api.get<AccountHubSummary>("/account/hub-summary"),
    // The affiliate offer for the buyer's own purchases. Asked for separately
    // from the order list, and only when the hub says there is something to ask
    // about — see the route comment for why it does not ride along.
    shareLinks: () => api.get<AccountShareLinks>("/account/share-links"),
    certificate: (token: string) =>
      api.get<CertificateData>(`/account/certificate/${token}`),
    confirmCertificate: (token: string, input: { full_name: string; ic_number: string }) =>
      api.post<CertificateData>(`/account/certificate/${token}/confirm`, input),
    profile: () => api.get<AccountProfile>("/account/profile"),
    updateProfile: (input: { full_name: string; ic_number?: string | null }) =>
      api.patch<AccountProfile>("/account/profile", {
        full_name: input.full_name,
        ic_number: input.ic_number ?? "",
      }),
    uploadAvatar: async (file: FormData) =>
      api.upload<{ avatar_url: string }>("/account/avatar", await maybeOptimizeImageFormData(file)),
    lms: (token: string) => api.get<LmsContent>(`/account/lms/${token}`),
    lmsProgress: (token: string, moduleIndex: number, completed: boolean) =>
      api.post<{ completed: number[] }>(`/account/lms/${token}/progress`, { moduleIndex, completed }),
  },

  community: {
    // Buyer / owner
    products: () => api.get<CommunityProductRef[]>("/community/products"),
    feed: (productId: string) => api.get<CommunityFeed>(`/community/${productId}/feed`),
    // Merged "Semua posting" feed across every space the caller can see.
    feedAll: (as: "seller" | "buyer") => api.get<CommunityFeed>(`/community/feed-all?as=${as}`),
    // Right-rail stats + leaderboard for one space ("all" = every space).
    about: (scope: string, as: "seller" | "buyer") =>
      api.get<CommunityAbout>(`/community/about?scope=${encodeURIComponent(scope)}&as=${as}`),
    // The caller's points + level + unlocked rewards within a space ("all" = across).
    level: (scope: string, as: "seller" | "buyer") =>
      api.get<CommunityLevel>(`/community/level?scope=${encodeURIComponent(scope)}&as=${as}`),
    // Seller reads/replaces their community's per-level rewards (10 slots).
    rewards: (productId: string) => api.get<CommunityLevelReward[]>(`/community/${productId}/rewards`),
    setRewards: (productId: string, rewards: { level: number; reward: string; coupon: string }[]) =>
      api.put<{ saved: number }>(`/community/${productId}/rewards`, { rewards }),
    comment: (productId: string, input: { body: string; postId?: string; parentId?: string }) => {
      const qs = input.postId && !input.parentId ? `?postId=${input.postId}` : "";
      return api.post<{ comment: CommunityComment; held: boolean }>(
        `/community/${productId}/comments${qs}`,
        { body: input.body, parentId: input.parentId ?? null },
      );
    },
    react: (input: { postId?: string; commentId?: string }) =>
      api.post<{ liked: boolean; like_count: number }>("/community/reactions", input),
    // Buyer creates a top-level post (held for seller approval).
    memberPost: (productId: string, body: string, imageKey?: string, category?: CommunityCategory) =>
      api.post<CommunityPost>(`/community/${productId}/member-posts`, {
        body,
        imageKey: imageKey || undefined,
        category,
      }),
    // Seller management
    manage: () => api.get<CommunityManageProduct[]>("/community/manage"),
    navEligible: () => api.get<{ eligible: boolean }>("/community/nav-eligible"),
    uploadPostImage: async (productId: string, file: FormData) =>
      api.upload<{ key: string }>(
        `/community/${productId}/posts/image`,
        await maybeOptimizeImageFormData(file),
      ),
    createPost: (
      productId: string,
      input: { body: string; imageKey?: string; notifyEmail?: boolean; category?: CommunityCategory; pinned?: boolean },
    ) =>
      api.post<CommunityPost>(`/community/${productId}/posts`, {
        body: input.body,
        imageKey: input.imageKey || undefined,
        notifyEmail: input.notifyEmail ?? true,
        category: input.category,
        pinned: input.pinned,
      }),
    // Sales pages worth ONE "Umum" announcement (2+ community products each).
    pageScopes: () => api.get<CommunityPageScope[]>("/community/page-scopes"),
    uploadPagePostImage: async (pageId: string, file: FormData) =>
      api.upload<{ key: string }>(
        `/community/pages/${pageId}/posts/image`,
        await maybeOptimizeImageFormData(file),
      ),
    createPagePost: (
      pageId: string,
      input: { body: string; imageKey?: string; notifyEmail?: boolean; category?: CommunityCategory; pinned?: boolean },
    ) =>
      api.post<CommunityPost>(`/community/pages/${pageId}/posts`, {
        body: input.body,
        imageKey: input.imageKey || undefined,
        notifyEmail: input.notifyEmail ?? true,
        category: input.category,
        pinned: input.pinned,
      }),
    // The whole store as ONE broadcast target; null when it runs fewer than two
    // communities (then the product's own composer already covers it).
    storeScope: () => api.get<CommunityStoreScope | null>("/community/store-scope"),
    uploadStorePostImage: async (file: FormData) =>
      api.upload<{ key: string }>(
        "/community/store/posts/image",
        await maybeOptimizeImageFormData(file),
      ),
    createStorePost: (input: {
      body: string;
      imageKey?: string;
      notifyEmail?: boolean;
      category?: CommunityCategory;
      pinned?: boolean;
    }) =>
      api.post<CommunityPost>("/community/store/posts", {
        body: input.body,
        imageKey: input.imageKey || undefined,
        notifyEmail: input.notifyEmail ?? true,
        category: input.category,
        pinned: input.pinned,
      }),
    deletePost: (postId: string) => api.delete<{ deleted: boolean }>(`/community/posts/${postId}`),
    setPin: (postId: string, pinned: boolean) =>
      api.patch<{ pinned: boolean }>(`/community/posts/${postId}/pin`, { pinned }),
    moderation: (productId: string) =>
      api.get<CommunityModerationItem[]>(`/community/${productId}/moderation`),
    pendingPosts: (productId: string) =>
      api.get<CommunityPendingPost[]>(`/community/${productId}/pending-posts`),
    approveComment: (commentId: string) =>
      api.post<{ approved: boolean }>(`/community/comments/${commentId}/approve`),
    approvePost: (postId: string) =>
      api.post<{ approved: boolean }>(`/community/posts/${postId}/approve`),
    removeComment: (commentId: string) =>
      api.delete<{ deleted: boolean }>(`/community/comments/${commentId}`),
    setKeywords: (productId: string, keywords: string[]) =>
      api.patch<{ keywords: string[] }>(`/community/${productId}/keywords`, { keywords }),
    setPosting: (productId: string, posting: "members" | "seller_only") =>
      api.patch<{ posting: string }>(`/community/${productId}/posting`, { posting }),
  },

  // Abandoned-checkout recovery (seller): captured checkouts + recovery stats.
  cartRecovery: {
    list: () => api.get<AbandonedCheckoutList>("/cart-recovery"),
    getSettings: (stage: FupStage = "abandoned") =>
      api.get<CartRecoverySettings>(`/cart-recovery/settings?stage=${stage}`),
    stats: (stage: FupStage = "abandoned") =>
      api.get<FupStats>(`/cart-recovery/stats?stage=${stage}`),
    pending: () => api.get<PendingFollowupList>("/cart-recovery/pending"),
    saveSettings: (body: CartRecoverySettingsInput) =>
      api.put<{ saved: boolean }>("/cart-recovery/settings", body),
    testWhatsApp: (body: CartRecoveryTestInput) =>
      api.post<{ sent: boolean; reason?: string }>("/cart-recovery/test-whatsapp", body),
  },

  iklan: {
    /** 127B S6: this seller's own ad coupon funnel. */
    kuponFunnel: (days: number) => api.get<import("@shared/adCoupon").KuponFunnelView>(`/iklan/kupon-funnel?days=${days}`),
    /** 127B: an optional email for a reminder before saved coupons run out. */
    remind: (tickets: string[], email: string) => api.post<{ saved: number }>("/iklan/remind", { tickets, email }, { public: true }),
    forYou: (
      q: { orderId?: string; token?: string; storeId?: string; surface?: string; slug?: string; vt?: string; src?: string; ticket?: string; start?: string },
      placement: IklanPlacement = "lepasbeli",
      session = false,
    ) => {
      const p = new URLSearchParams({ placement });
      for (const [k, v] of Object.entries(q)) if (v) p.set(k, v);
      return api.get<ForYouResponse>(`/iklan/for-you?${p.toString()}`, session ? { optionalAuth: true } : { public: true });
    },
    claim: (offerId: string, orderId: string, placement: IklanPlacement = "lepasbeli") =>
      api.post<ClaimedAdCoupon>("/iklan/claim", { offerId, orderId, placement }, { public: true }),
    manager: (julat: string, breakdown: Breakdown) =>
      api.get<ManagerView>(`/iklan/manager?${julat}&breakdown=${breakdown}`),
    visitor: () => api.post<{ token: string }>("/iklan/visitor", {}, { public: true }),
    /** HQ's own offer under a seller's support ticket, or null. */
    tiket: () => api.get<{ title: string; text: string; href: string } | null>("/iklan/tiket"),
    campaignOptions: () => api.get<CampaignOptions>("/iklan/campaigns/options"),
    createCampaign: (body: CampaignInput) => api.post<{ id: string }>("/iklan/campaigns", body),
    /** The shop chat a Mesej ad opens (117C S4): opened by the buyer's order or the page's pass, then by its token. */
    chatStart: (body: { offerId: string; placement: IklanPlacement; orderId?: string; pass?: string }) =>
      api.post<import("@shared/iklanChat").ChatStart>("/iklan/chat/start", body),
    chat: (token: string) => api.get<import("@shared/iklanChat").ChatView>(`/iklan/chat?token=${encodeURIComponent(token)}`),
    chatSend: (token: string, text: string, captcha?: string) =>
      api.post<import("@shared/iklanChat").ChatView>("/iklan/chat/message", { token, text, captcha }, { timeoutMs: CHAT_CLIENT_WAIT_MS }),
    chatEmail: (token: string, email: string) => api.post<import("@shared/iklanChat").ChatView>("/iklan/chat/email", { token, email }),
    /** The private advertiser door (117C S8). */
    adv: {
      invite: (j: string) => api.get<import("@shared/iklanAdv").AdvInviteView>(`/iklan/adv/invite?j=${encodeURIComponent(j)}`, { public: true }),
      code: (j: string) => api.post<{ sent: true; email: string }>("/iklan/adv/code", { j }, { public: true }),
      accept: (j: string, profile: import("@shared/iklanAdv").AdvProfileInput) => api.post<import("@shared/iklanAdv").AdvMe>("/iklan/adv/accept", { j, profile }),
      me: () => api.get<import("@shared/iklanAdv").AdvMe>("/iklan/adv/me"),
      saveMe: (profile: import("@shared/iklanAdv").AdvProfileInput) => api.put<import("@shared/iklanAdv").AdvMe>("/iklan/adv/me", profile),
    },
    /** Pembantu Sellhiz (117C S7): this week's Mesej Ditaja opened, or hidden. */
    ditaja: (id: string, action: "open" | "hide") => api.post<{ ok: true }>(`/iklan/pembantu/ditaja/${id}`, { action }),
    /** Peti Mesej (117C S5): the seller's side of the shop chats. */
    inboxCount: () => api.get<{ total: number; pending: number }>("/iklan/inbox/count"),
    inbox: (tab: "semua" | "perlu" | "ai" = "semua") => api.get<import("@shared/iklanChat").InboxList>(`/iklan/inbox?tab=${tab}`),
    inboxThread: (chatId: string) => api.get<import("@shared/iklanChat").InboxThread>(`/iklan/inbox/${encodeURIComponent(chatId)}`),
    inboxReply: (chatId: string, text: string, productId: string | null = null) =>
      api.post<import("@shared/iklanChat").InboxThread>(`/iklan/inbox/${encodeURIComponent(chatId)}/reply`, { text, productId }),
    inboxAi: (chatId: string, on: boolean) => api.post<import("@shared/iklanChat").InboxThread>(`/iklan/inbox/${encodeURIComponent(chatId)}/ai`, { on }),
    /** A Trafik card's picture (117C S3): optimised to WebP/AVIF first, kept under the store's own prefix. */
    creativeImage: async (file: FormData) => api.upload<{ key: string }>("/iklan/creative-image", await maybeOptimizeImageFormData(file)),
    setCampaignOn: (id: string, on: boolean) => api.patch<{ id: string; on: boolean }>(`/iklan/manager/campaigns/${encodeURIComponent(id)}`, { on }),
    deleteCampaigns: (ids: string[]) => api.post<BulkResult>("/iklan/manager/campaigns/delete", { ids }),
    restoreCampaigns: (ids: string[]) => api.post<BulkResult>("/iklan/manager/campaigns/restore", { ids }),
    setOn: (offerId: string, on: boolean) =>
      api.patch<{ id: string; on: boolean }>(`/iklan/manager/offers/${encodeURIComponent(offerId)}`, { on }),
    wallet: () => api.get<WalletView>("/iklan/wallet"),
    redeemKredit: (code: string) => api.post<{ amountCents: number; expiresAt: string; wallet: WalletView }>("/iklan/wallet/kredit", { code }),
    topup: (amount: number, method?: TopupMethod, phone?: string) =>
      api.post<{ topupId: string; paymentUrl: string; method: TopupMethod }>("/iklan/wallet/topup", { amount, method, ...(phone ? { phone } : {}) }),
    topupStatus: (id: string) =>
      api.get<{ status: "paid" | "failed" | "pending"; wallet: WalletView }>(`/iklan/wallet/topup/${encodeURIComponent(id)}`),
  },
  bonus: {
    // Buyer
    offers: () => api.get<BonusOffer[]>("/bonus/offers"),
    uploadProof: async (productId: string, file: FormData) =>
      api.upload<{ key: string }>(
        `/bonus/${productId}/proof-image`,
        await maybeOptimizeImageFormData(file),
      ),
    uploadReviewPhoto: async (productId: string, file: FormData) =>
      api.upload<{ key: string }>(
        `/bonus/${productId}/review-photo`,
        await maybeOptimizeImageFormData(file),
      ),
    claim: (productId: string, proofType: "image" | "link", proofValue: string) =>
      api.post<{ status: string; tier: string }>(`/bonus/${productId}/claim`, {
        proofType,
        proofValue,
      }),
    rewardFile: (productId: string, blockId: string) =>
      api.blob(`/bonus/${productId}/reward-file/${blockId}`),
    claimReview: (
      productId: string,
      review: { rating: number; reviewText?: string; photoKeys?: string[] },
    ) =>
      api.post<{ status: string; tier: string }>(`/bonus/${productId}/claim`, {
        proofType: "review",
        ...review,
      }),
    // Seller
    review: () => api.get<BonusClaim[]>("/bonus/review"),
    proofImage: (claimId: string) => api.blob(`/bonus/claims/${claimId}/image`),
    reviewPhoto: (claimId: string, idx: number) =>
      api.blob(`/bonus/claims/${claimId}/review-photo/${idx}`),
    revoke: (ids: string[], reason?: string) =>
      api.post<{ revoked: number }>("/bonus/revoke", { ids, reason }),
    restore: (ids: string[]) => api.post<{ restored: number }>("/bonus/restore", { ids }),
    setHidden: (ids: string[], hidden: boolean) =>
      api.post<{ updated: number }>("/bonus/visibility", { ids, hidden }),
    // Seller: Review & Rating summary
    overview: () => api.get<BonusOverview>("/bonus/overview"),
    assign: (productIds: string[], kind: "inherit" | "none") =>
      api.post<{ applied: number }>("/bonus/assign", { productIds, kind }),
    // Seller: review groups — which products share testimonials (0138)
    mergeReviewGroup: (productIds: string[]) =>
      api.post<{ group_id: string; updated: number }>("/bonus/review-groups/merge", { productIds }),
    splitReviewGroup: (productIds: string[]) =>
      api.post<{ updated: number }>("/bonus/review-groups/split", { productIds }),
    // Seller: the page review pool — which sales pages show what they earned (0139)
    pagesOverview: () => api.get<BonusPagesOverview>("/bonus/pages/overview"),
    sharePages: (pageIds: string[], share: boolean) =>
      api.post<{ updated: number; share: boolean }>("/bonus/pages/share", { pageIds, share }),
    // Seller: reward builder (Bonus Review tab)
    rewardProducts: () => api.get<BonusRewardProduct[]>("/bonus/reward-products"),
    defaultReward: () => api.get<{ blocks: BonusRewardBlock[] }>("/bonus/default-reward"),
    saveDefaultReward: (blocks: BonusRewardBlock[]) =>
      api.post<{ blocks: BonusRewardBlock[] }>("/bonus/default-reward", { blocks }),
    uploadRewardFile: (productId: string, file: FormData) =>
      api.upload<{ key: string; name: string }>(`/bonus/${productId}/reward-file`, file),
    uploadRewardFileGeneric: (file: FormData) =>
      api.upload<{ key: string; name: string }>("/bonus/reward-upload", file),
    saveReward: (payload: {
      productIds: string[];
      mode: "review" | "external" | "both";
      title?: string;
      postUrl?: string;
      instructions?: string;
      blocks: BonusRewardBlock[];
      blocksExt: BonusRewardBlock[];
    }) => api.post<{ applied: number }>("/bonus/reward", payload),
    // Buyer: earned review-reward coupons for the seller who owns a product
    // (checkout reminder). Silent 401 when the buyer isn't logged in.
    myCoupons: (productId: string) =>
      api.get<{ coupons: MyReviewCoupon[] }>(`/bonus/my-coupons?productId=${encodeURIComponent(productId)}`, {
        optionalAuth: true,
      }),
  },

  // Operator console (super-admin). All endpoints 403 for non-operators.
  admin: {
    session: () => api.get<{ isPlatformAdmin: boolean; email: string }>("/admin/session"),
    overview: (julat: string) => api.get<AdminOverview>(`/admin/overview?${julat}`),
    sellers: {
      list: (qs = "") => api.get<AdminSellerList>(`/admin/sellers${qs}`),
      get: (id: string) => api.get<AdminSellerDetail>(`/admin/sellers/${id}`),
      suspend: (id: string) => api.post<{ suspended: boolean }>(`/admin/sellers/${id}/suspend`),
      unsuspend: (id: string) => api.post<{ suspended: boolean }>(`/admin/sellers/${id}/unsuspend`),
      impersonate: (id: string) =>
        api.post<AdminImpersonateResult>(`/admin/sellers/${id}/impersonate`),
      setPlan: (id: string, body: AdminAssignPlanInput) =>
        api.patch<{ plan: string; planExpiresAt: string | null }>(`/admin/sellers/${id}/plan`, body),
      extendPlan: (id: string, months: number) =>
        api.post<{ plan: string; planExpiresAt: string | null }>(`/admin/sellers/${id}/plan/extend`, {
          months,
        }),
      bulkSuspend: (ids: string[], suspend: boolean) =>
        api.post<{ updated: number; skipped: string[] }>("/admin/sellers/bulk-suspend", {
          ids,
          suspend,
        }),
      bulkSetPlan: (body: AdminBulkAssignPlanInput) =>
        api.patch<{ updated: number; plan: string; planExpiresAt: string | null }>(
          "/admin/sellers/bulk-plan",
          body,
        ),
    },
    resellers: {
      list: () => api.get<AdminResellerRow[]>("/admin/resellers"),
      get: (id: string) => api.get<AdminResellerDetail>(`/admin/resellers/${id}`),
      channel: () => api.get<AdminResellerChannel>("/admin/resellers/channel"),
      update: (
        id: string,
        body: { commissionPct?: number; creditEnabled?: boolean; status?: "active" | "suspended" },
      ) => api.patch<{ updated: boolean }>(`/admin/resellers/${id}`, body),
      bulkUpdate: (body: AdminResellerBulkInput) =>
        api.patch<{ updated: number; skipped: string[] }>("/admin/resellers/bulk", body),
      bulkRelease: (ids: string[]) =>
        api.post<{ released: number }>("/admin/resellers/bulk-release", { ids }),
      grantCredit: (id: string, amountCents: number, note?: string) =>
        api.post<{ walletCents: number }>(`/admin/resellers/${id}/credit`, { amountCents, note }),
      releaseClaims: (id: string) =>
        api.post<{ released: number }>(`/admin/resellers/${id}/release-claims`, undefined),
    },
    sites: {
      list: (qs = "") => api.get<AdminSiteList>(`/admin/sites${qs}`),
      create: (body: AdminSiteCreateInput) =>
        api.post<Store & { promotedExistingUser?: boolean }>("/admin/sites", body),
      ownerLookup: (email: string) =>
        api.get<AdminSiteOwnerLookup>(`/admin/sites/owner-lookup?email=${encodeURIComponent(email)}`),
      get: (id: string) => api.get<AdminSiteDetail>(`/admin/sites/${id}`),
      update: (id: string, body: AdminSiteUpdateInput) =>
        api.patch<Store>(`/admin/sites/${id}`, body),
      setMasterTemplate: (id: string, isMaster: boolean) =>
        api.patch<Store>(`/admin/sites/${id}/master-template`, { isMaster }),
      setLiveChime: (id: string, enabled: boolean) =>
        api.patch<Store>(`/admin/sites/${id}/live-chime`, { enabled }),
      addDomain: (id: string, domain: string) =>
        api.post<Store & { dnsAutoAdded?: boolean }>(`/admin/sites/${id}/custom-domain`, { domain }),
      verifyDomain: (id: string) =>
        api.post<Store & { dnsAutoAdded?: boolean }>(`/admin/sites/${id}/custom-domain/verify`, undefined),
      removeDomain: (id: string) => api.delete<Store>(`/admin/sites/${id}/custom-domain`),
      members: (id: string) => api.get<StoreMember[]>(`/admin/sites/${id}/members`),
      invite: (id: string, body: StoreMemberInviteInput) =>
        api.post<StoreInviteResult>(`/admin/sites/${id}/members`, body),
      revokeMember: (id: string, memberId: string) =>
        api.delete<{ revoked: boolean }>(`/admin/sites/${id}/members/${memberId}`),
      updatePixel: (id: string, body: AdminPixelUpdateInput) =>
        api.patch<AdminSitePixel>(`/admin/sites/${id}/pixel`, body),
      updateGateways: (id: string, body: AdminGatewayUpdateInput) =>
        api.patch<AdminSiteGateways>(`/admin/sites/${id}/gateways`, body),
      importSources: () => api.get<AdminImportablePage[]>("/admin/sites/import-sources"),
      importPage: (id: string, sourcePageId: string) =>
        api.post<AdminImportPageResult>(`/admin/sites/${id}/import-page`, { sourcePageId }),
      bulkImport: (body: { storeIds: string[]; sourcePageIds: string[] }) =>
        api.post<AdminBulkSyncResult>("/admin/sites/bulk-import", body),
      bulkOverride: (body: { storeIds: string[]; sourcePageIds: string[] }) =>
        api.post<AdminBulkSyncResult>("/admin/sites/bulk-override", body),
      bulkPublish: (body: { storeIds: string[]; sourcePageIds?: string[] }) =>
        api.post<AdminBulkSyncResult>("/admin/sites/bulk-publish", body),
    },
    // Analitik Halaman: behaviour on imported sales pages (D1 sellhiz-analytics).
    insights: {
      copies: () =>
        api.get<{
          copies: {
            slug: string;
            title: string | null;
            sessions30d: number;
            lastAt: string | null;
            previewUrl: string;
          }[];
        }>("/admin/insights"),
      // `version` reads one version of the copy ("b", "ai…"); omitted = A.
      overview: (slug: string, days: number, device: string, view: InsightsView = {}) =>
        api.get<InsightsOverview>(
          `/admin/insights/${encodeURIComponent(slug)}?days=${days}&device=${encodeURIComponent(device)}${insightsViewQuery(view)}`,
        ),
      evidence: (slug: string, days: number, device: string, view: InsightsView = {}, intensity?: AiIntensity) =>
        api.get<InsightsEvidence>(
          `/admin/insights/${encodeURIComponent(slug)}/evidence?days=${days}&device=${encodeURIComponent(device)}${insightsViewQuery(view)}${intensity ? `&intensity=${intensity}` : ""}`,
        ),
      // Ketumpatan AI: one setting for every AI that writes or judges a version.
      aiIntensity: () => api.get<{ level: AiIntensity }>("/admin/insights/settings/ai-intensity"),
      setAiIntensity: (level: AiIntensity) => api.put<{ level: AiIntensity }>("/admin/insights/settings/ai-intensity", { level }),
      versions: (slug: string, days = 90) =>
        api.get<{ versions: InsightsVersion[] }>(`/admin/insights/${encodeURIComponent(slug)}/versions?days=${days}`),
      setLabel: (body: { kind: "campaign" | "ad"; key: string; label: string }) =>
        api.put<{ kind: string; key: string; label: string }>("/admin/insights/labels", body),
      heat: (slug: string, days: number, device: string, view: InsightsView = {}) =>
        api.get<{ points: InsightsHeatPoint[] }>(
          `/admin/insights/${encodeURIComponent(slug)}/heat?days=${days}&device=${encodeURIComponent(device)}${insightsViewQuery(view)}`,
        ),
      // Versi B: the page with an AI's edits over it, previewed beside A.
      variant: (slug: string) => api.get<PageVariantView>(`/admin/insights/${encodeURIComponent(slug)}/variant`),
      version: (slug: string, name: string) =>
        api.get<PageVersionView>(`/admin/insights/${encodeURIComponent(slug)}/versions/${encodeURIComponent(name)}`),
      versionFunnels: (slug: string, name: string) =>
        api.get<{ source: string; funnels: FunnelPublishTarget[] }>(
          `/admin/insights/${encodeURIComponent(slug)}/versions/${encodeURIComponent(name)}/funnels`,
          { timeoutMs: 60_000 },
        ),
      copyFunnels: (slug: string) =>
        api.get<{ funnels: FunnelPublishTarget[] }>(`/admin/insights/${encodeURIComponent(slug)}/funnels`, { timeoutMs: 60_000 }),
      // The AI writes a whole version before answering: about a minute.
      optimizeNow: (slug: string, body: { funnelId: string; from: string; share: number }) =>
        api.post<FunnelOptimizeResult>(`/admin/insights/${encodeURIComponent(slug)}/optimize`, body, { timeoutMs: 240_000 }),
      publishVersion: (slug: string, name: string, body: { funnelId: string; replace?: ArmKey | null }) =>
        api.post<{ key: ArmKey; replaced: string | null; funnelId: string }>(
          `/admin/insights/${encodeURIComponent(slug)}/versions/${encodeURIComponent(name)}/publish`,
          body,
          { timeoutMs: 60_000 },
        ),
      generateVariant: (slug: string, days: number, device: string) =>
        api.post<PageVariantView>(
          `/admin/insights/${encodeURIComponent(slug)}/variant/generate`,
          { days, device },
          // Claude writing the edits takes a minute or more.
          { timeoutMs: 200_000 },
        ),
      updateVariant: (slug: string, ops: VariantUpdateInput["ops"]) =>
        api.put<PageVariantView>(`/admin/insights/${encodeURIComponent(slug)}/variant`, { ops }),
      deleteVariant: (slug: string) =>
        api.delete<{ deleted: boolean }>(`/admin/insights/${encodeURIComponent(slug)}/variant`),
      aiKey: () => api.get<InsightsAiKeyStatus>("/admin/insights/settings/ai-key"),
      saveAiKey: (key: string) => api.put<InsightsAiKeyStatus>("/admin/insights/settings/ai-key", { key }),
      deleteAiKey: () => api.delete<InsightsAiKeyStatus>("/admin/insights/settings/ai-key"),
    },
    // Tab Iklan: every ad account the Meta token reads, spend beside real paid
    // orders, and the rules and AI keys for the AI that will run the ads.
    ads: {
      overview: (q: { julat: string; slug?: string | null; account?: string | null }) => {
        const qs = new URLSearchParams(q.julat);
        if (q.slug) qs.set("slug", q.slug);
        if (q.account) qs.set("account", q.account);
        return api.get<AdsOverview>(`/admin/ads/overview?${qs}`);
      },
      sync: () =>
        api.post<{
          labels: { asked: number; named: number; missed: number };
          spend: { accounts: number; rows: number; failed: number };
          structure: { accounts: number; entities: number; changes: number; snapshots: number; failed: number };
          creatives: { accounts: number; read: number; errors: string[] };
          partsFailed: number;
        }>("/admin/ads/sync", {}, { timeoutMs: 120_000 }),
      settings: () => api.get<AdsSettingsView>("/admin/ads/settings"),
      saveRules: (rules: AdsRulesInput) => api.put<AdsSettingsView>("/admin/ads/settings/rules", rules),
      saveAi: (ai: AdsAiSettingsInput) => api.put<AdsSettingsView>("/admin/ads/settings/ai", ai),
      setAccount: (accountId: string, controlled: boolean) =>
        api.put<AdsSettingsView>("/admin/ads/settings/accounts", { accountId, controlled }),
      tokenCheck: () => api.post<AdsSettingsView>("/admin/ads/token-check", {}, { timeoutMs: 60_000 }),
      saveKey: (provider: AiProvider, key: string) => api.put<AdsSettingsView>(`/admin/ads/ai-keys/${provider}`, { key }),
      deleteKey: (provider: AiProvider) => api.delete<AdsSettingsView>(`/admin/ads/ai-keys/${provider}`),
      savePersonalToken: (token: string) => api.put<AdsSettingsView>("/admin/ads/personal-token", { token }, { timeoutMs: 60_000 }),
      checkPersonalToken: () => api.post<AdsSettingsView>("/admin/ads/personal-token/check", {}, { timeoutMs: 60_000 }),
      deletePersonalToken: () => api.delete<AdsSettingsView>("/admin/ads/personal-token"),
      decisions: (account?: string | null) =>
        api.get<AdsDecisionsView>(`/admin/ads/decisions${account ? `?account=${encodeURIComponent(account)}` : ""}`),
      // A change to Meta: checked by Meta first, then sent, logged and undoable.
      act: (body: { entityId: string; kind: AdsActionKind; params: AdsActionParams; reason?: string; decisionKey?: string }) =>
        api.post<AdsActionRecord>("/admin/ads/actions", body, { timeoutMs: 60_000 }),
      undo: (id: string) => api.post<AdsActionRecord>(`/admin/ads/actions/${id}/undo`, {}, { timeoutMs: 60_000 }),
      actions: () => api.get<{ actions: AdsActionRecord[] }>("/admin/ads/actions"),
      dismiss: (key: string, hours = 24) => api.post<{ key: string; hours: number }>("/admin/ads/dismissals", { key, hours }),
      restoreDismissed: () => api.delete<{ cleared: boolean }>("/admin/ads/dismissals"),
      audiences: (account: string) =>
        api.get<{ audiences: AdsAudience[]; pixels: AdsPixel[] }>(`/admin/ads/audiences?account=${encodeURIComponent(account)}`),
      // Buyers are hashed on the server (SHA-256) before anything goes to Meta.
      createAudience: (account: string, body: AdsAudienceInput) =>
        api.post<{ id: string; uploaded: number | null }>(`/admin/ads/audiences?account=${encodeURIComponent(account)}`, body, {
          timeoutMs: 120_000,
        }),
      targeting: (adsetId: string) =>
        api.get<{ targeting: Record<string, unknown> }>(`/admin/ads/adsets/${encodeURIComponent(adsetId)}/targeting`),
      // The AI: judge today's cards now (and act while "AI ambil alih" is on).
      aiRun: (account?: string | null) =>
        api.post<AdsAiRunView>(`/admin/ads/ai/run${account ? `?account=${encodeURIComponent(account)}` : ""}`, {}, { timeoutMs: 240_000 }),
      aiLatest: () => api.get<{ run: AdsAiRunView | null }>("/admin/ads/ai/latest"),
      // Pantau laju: how closely the ads are watched now, and "Semak sekarang".
      pulse: () => api.get<PulseView>("/admin/ads/pulse"),
      pulseRun: () => api.post<PulseView>("/admin/ads/pulse/run", {}, { timeoutMs: 240_000 }),
      aiAsk: (question: string, account?: string | null) =>
        api.post<{ answer: string; provider: AiProvider; model: string }>(
          `/admin/ads/ai/ask${account ? `?account=${encodeURIComponent(account)}` : ""}`,
          { question },
          { timeoutMs: 120_000 },
        ),
      aiBrief: (account?: string | null) =>
        api.get<{ text: string; cards: number }>(`/admin/ads/ai/brief${account ? `?account=${encodeURIComponent(account)}` : ""}`),
      aiExternal: (body: AdsExternalInput, account?: string | null) =>
        api.post<AdsAiRunView>(`/admin/ads/ai/external${account ? `?account=${encodeURIComponent(account)}` : ""}`, body, { timeoutMs: 120_000 }),
      setTakeover: (body: AdsTakeoverInput) => api.put<AdsSettingsView>("/admin/ads/takeover", body),
      setNewAds: (body: AdsNewAdsInput) => api.put<AdsSettingsView>("/admin/ads/takeover/new-ads", body),
      // The creative autopilot stopped on a refusal that kept coming back: try again once its cause is fixed.
      retryAutopilot: () => api.post<AdsSettingsView>("/admin/ads/autopilot/retry", {}),
      saveSkills: (body: AdsSkillsInput) => api.put<AdsSettingsView>("/admin/ads/settings/skills", body),
      // The Claude Code lane's token: made (shown once) or switched off.
      issueCcToken: () => api.post<{ token: string; settings: AdsSettingsView }>("/admin/ads/cc-token", {}),
      revokeCcToken: () => api.delete<AdsSettingsView>("/admin/ads/cc-token"),
      setLock: (campaignId: string, locked: boolean) => api.put<{ locks: string[] }>("/admin/ads/locks", { campaignId, locked }),
      resetCap: (account: string) => api.delete<AdsSettingsView>(`/admin/ads/caps/${encodeURIComponent(account)}`),
      // The AI's record and what it teaches.
      learning: () => api.get<AdsLearningView>("/admin/ads/learning"),
      // Kreatif: tiring creatives and how each has paid; new words, made into a paused ad on the operator's yes.
      creatives: (account?: string | null) =>
        api.get<AdsCreativeInsight>(`/admin/ads/creatives${account ? `?account=${encodeURIComponent(account)}` : ""}`),
      analyzeCreatives: (account?: string | null) =>
        api.post<{ analysed: number; errors: string[] }>(`/admin/ads/creatives/analyze${account ? `?account=${encodeURIComponent(account)}` : ""}`, {}, { timeoutMs: 240_000 }),
      compose: (body: { account: string; offer: string; link: string; language: "bm" | "en" | "campur"; count?: number }) =>
        api.post<{ drafts: AdDraft[]; provider: AiProvider; winning: WinningRow[] }>("/admin/ads/creatives/compose", body, { timeoutMs: 120_000 }),
      accountImages: (account: string) => api.get<{ images: AccountImage[] }>(`/admin/ads/creatives/images?account=${encodeURIComponent(account)}`),
      generateImage: (body: { account: string; prompt: string; style: "poster" | "produk" | "foto"; aspect: "1:1" | "4:5" | "9:16" }) =>
        api.post<AccountImage & { provider: AiProvider }>("/admin/ads/creatives/images", body, { timeoutMs: 240_000 }),
      launchAdsets: (account: string) =>
        api.get<{ adsets: { id: string; label: string; status: string; daily_budget: number | null; campaign: string | null }[] }>(
          `/admin/ads/creatives/adsets?account=${encodeURIComponent(account)}`,
        ),
      launch: (body: AdsLaunchInput) => api.post<{ adId: string; creativeId: string; adName: string }>("/admin/ads/creatives/launch", body, { timeoutMs: 120_000 }),
      // Klon ke akaun lain: one clone call per account, so each shows its own progress and a failure stays its own.
      cloneCampaigns: (account?: string | null) =>
        api.get<{ campaigns: CloneCampaignCard[] }>(`/admin/ads/clone/campaigns${account ? `?account=${encodeURIComponent(account)}` : ""}`),
      cloneTargets: (campaignIds: string[]) => api.get<CloneTargetsView>(`/admin/ads/clone/targets?campaigns=${campaignIds.join(",")}`, { timeoutMs: 90_000 }),
      clone: (body: AdsCloneInput) => api.post<CloneResult>("/admin/ads/clone", body, { timeoutMs: 180_000 }),
      cloneLive: (campaignIds: string[], live: boolean) =>
        api.post<{ campaignIds: string[]; live: boolean }>("/admin/ads/clone/live", { campaignIds, live }, { timeoutMs: 120_000 }),
      cloneUndo: (campaignIds: string[]) => api.post<{ campaignIds: string[] }>("/admin/ads/clone/undo", { campaignIds }, { timeoutMs: 120_000 }),
      variations: (adId: string, count = 3) =>
        api.post<{ variations: TextVariation[]; provider: AiProvider }>(`/admin/ads/creatives/${encodeURIComponent(adId)}/variations`, { count }, { timeoutMs: 120_000 }),
      newAd: (adId: string, body: { primary: string; headline: string }) =>
        api.post<{ adId: string; creativeId: string; adName: string }>(`/admin/ads/creatives/${encodeURIComponent(adId)}/new-ad`, body, { timeoutMs: 90_000 }),
      saveReport: (body: { on: boolean; to: string[] }) => api.put<AdsSettingsView>("/admin/ads/settings/report", body),
      metaRules: () => api.get<MetaRulesView>("/admin/ads/meta-rules", { timeoutMs: 60_000 }),
      setMetaRule: (id: string, enabled: boolean) => api.put<MetaRuleView>(`/admin/ads/meta-rules/${encodeURIComponent(id)}`, { enabled }),
      suggestMetaRule: (id: string) => api.post<MetaRuleSuggestion>(`/admin/ads/meta-rules/${encodeURIComponent(id)}/suggest`, {}, { timeoutMs: 180_000 }),
      applyMetaRule: (id: string, body: RuleEdit & { basedOn: string }) => api.put<MetaRuleView>(`/admin/ads/meta-rules/${encodeURIComponent(id)}/conditions`, body),
      undoMetaRule: (id: string) => api.post<MetaRuleView>(`/admin/ads/meta-rules/${encodeURIComponent(id)}/conditions/undo`, {}),
      sendReport: () => api.post<{ sent: number; skipped?: string }>("/admin/ads/report/send", {}, { timeoutMs: 120_000 }),
    },
    // Funnel: a superadmin store's page split between two to four versions of its
    // sales page, each followed from the visit to the paid order.
    cashflow: {
      overview: (julat: string) => api.get<CashflowCube>(`/admin/cashflow/overview?${julat}`, { timeoutMs: 60_000 }),
      pulse: () => api.get<CashflowPulse>("/admin/cashflow/pulse"),
      hours: () => api.get<CashflowHours>("/admin/cashflow/hours", { timeoutMs: 60_000 }),
      insights: (julat: string) => api.get<CashflowInsights>(`/admin/cashflow/insights?${julat}`, { timeoutMs: 60_000 }),
      setAccount: (accountId: string, included: boolean) =>
        api.put<{ accounts: Record<string, boolean> }>("/admin/cashflow/settings/accounts", { accountId, included }),
      settings: () => api.get<{ settings: CashflowSettingsInput; products: CashflowProduct[] }>("/admin/cashflow/settings"),
      saveCosts: (costs: CashflowCostsInput) => api.put<{ settings: CashflowSettingsInput }>("/admin/cashflow/settings/costs", costs),
      setRole: (storeId: string, role: CashflowRole) => api.put<{ settings: CashflowSettingsInput }>("/admin/cashflow/settings/roles", { storeId, role }),
      sources: () => api.get<{ sources: CashflowSourceView[] }>("/admin/cashflow/sources"),
      createSource: (input: CashflowSourceCreateInput) => api.post<{ source: CashflowSourceView; secret: string }>("/admin/cashflow/sources", input),
      updateSource: (id: string, patch: CashflowSourcePatchInput) => api.put<{ sources: CashflowSourceView[] }>(`/admin/cashflow/sources/${id}`, patch),
      deleteSource: (id: string) => api.delete<{ sources: CashflowSourceView[] }>(`/admin/cashflow/sources/${id}`),
      sourceSecret: (id: string) => api.get<{ secret: string }>(`/admin/cashflow/sources/${id}/secret`),
      setSourceSecret: (id: string, secret: string) => api.put<{ secret: string }>(`/admin/cashflow/sources/${id}/secret`, { secret }),
      deliveries: (id: string) => api.get<{ deliveries: HookDelivery[] }>(`/admin/cashflow/sources/${id}/deliveries`),
      testSource: (id: string) => api.post<{ delivery: HookDelivery }>(`/admin/cashflow/sources/${id}/test`, {}),
      ai: () => api.get<CashflowAiView>("/admin/cashflow/ai"),
      aiPlan: () => api.post<CashflowAiView>("/admin/cashflow/ai/plan", {}, { timeoutMs: 180_000 }),
      aiAsk: (question: string) => api.post<{ answer: string; provider: string; model: string }>("/admin/cashflow/ai/ask", { question }, { timeoutMs: 120_000 }),
      saveAi: (ai: CashflowAiSettingsInput) => api.put<CashflowAiView>("/admin/cashflow/settings/ai", ai),
      actions: () => api.get<{ actions: CashflowActionView[]; stores: { id: string; name: string }[] }>("/admin/cashflow/ai/actions"),
      act: (body: { planId: string; actionKey: string; kind: "done" | "offer" | "ads"; storeId?: string }) =>
        api.post<{ action: CashflowActionView }>("/admin/cashflow/ai/act", body, { timeoutMs: 60_000 }),
      undoAction: (id: string) => api.post<{ actions: CashflowActionView[] }>(`/admin/cashflow/ai/actions/${id}/undo`, {}),
      meta: () => api.get<MetaConnectStatus>("/admin/cashflow/meta"),
      metaConnect: () => api.post<{ url: string }>("/admin/cashflow/meta/connect", {}),
      metaCheck: () => api.post<MetaConnectStatus>("/admin/cashflow/meta/check", {}, { timeoutMs: 60_000 }),
      metaRemove: (id: string) => api.delete<MetaConnectStatus>(`/admin/cashflow/meta/connections/${id}`),
      upsell: () => api.get<UpsellView>("/admin/cashflow/upsell", { timeoutMs: 60_000 }),
      upsellPulse: (fe: string[], launch: string[]) =>
        api.get<UpsellPulse>(`/admin/cashflow/upsell/pulse?fe=${encodeURIComponent(fe.join(","))}&launch=${encodeURIComponent(launch.join(","))}`),
      saveCycle: (key: string, edit: CashflowUpsellCycleInput) => api.put<UpsellView>(`/admin/cashflow/upsell/cycles/${key}`, edit, { timeoutMs: 60_000 }),
      addCycle: (edit: CashflowUpsellNewInput) => api.post<{ key: string; view: UpsellView }>("/admin/cashflow/upsell/cycles", edit, { timeoutMs: 60_000 }),
      resetCycle: (key: string) => api.delete<UpsellView>(`/admin/cashflow/upsell/cycles/${key}`, { timeoutMs: 60_000 }),
      markBig: (productId: string, big: boolean | null) => api.put<UpsellView>("/admin/cashflow/upsell/big", { productId, big }, { timeoutMs: 60_000 }),
      saveEvent: (key: string, edit: { label?: string; relaunch?: string[] | null }) =>
        api.put<UpsellView>(`/admin/cashflow/upsell/events/${key}`, edit, { timeoutMs: 60_000 }),
    },
    funnels: {
      list: () => api.get<{ funnels: FunnelReport[] }>("/admin/funnels"),
      stores: () => api.get<{ stores: FunnelStoreOption[] }>("/admin/funnels/stores"),
      options: (storeId: string) =>
        api.get<{ pages: FunnelPageOption[]; sources: FunnelSourceOption[] }>(
          `/admin/funnels/stores/${encodeURIComponent(storeId)}/options`,
        ),
      // The store's FYP posts a version can show; `pageId` says which bag products that page does not sell.
      fyp: (storeId: string, pageId?: string | null) =>
        api.get<FunnelFypOptions>(
          `/admin/funnels/stores/${encodeURIComponent(storeId)}/fyp${pageId ? `?page=${encodeURIComponent(pageId)}` : ""}`,
        ),
      // The server checks every field again (store, page, versions, shares).
      create: (body: { name: string; storeId: string; pageId: string; sources: string[] } & FunnelTrafficBody) =>
        api.post<FunnelReport>("/admin/funnels", body),
      /** A `window` (Sebelum / Selepas perubahan, the AI's fixed spans) or the page's date range as a query (121L). */
      report: (id: string, window: FunnelWindow | null, campaign?: string | null, julat?: string) =>
        api.get<FunnelReport>(
          `/admin/funnels/${encodeURIComponent(id)}?${window ? `window=${window}` : julat ?? ""}${campaign ? `&campaign=${encodeURIComponent(campaign)}` : ""}`,
        ),
      setStatus: (id: string, status: "running" | "paused" | "ended") =>
        api.patch<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/status`, { status }),
      promote: (id: string, key: string) =>
        api.post<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/promote`, { key }),
      // The hourly decision, now: Auto-optimize's shares, or auto-stop's winner.
      tick: (id: string) =>
        api.post<{ result: { action: "promoted" | "reweighted" | "unchanged"; winner?: string; weights?: number[] }; report: FunnelReport }>(
          `/admin/funnels/${encodeURIComponent(id)}/tick`,
          {},
        ),
      saveTraffic: (id: string, body: FunnelTrafficBody) =>
        api.put<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/traffic`, body),
      // Where this operator put the board's blocks (null = the default arrangement).
      campaigns: (id: string, days: FunnelCampaignDays) =>
        api.get<FunnelCampaignsReport>(`/admin/funnels/${encodeURIComponent(id)}/campaigns?days=${days}`),
      refreshCampaigns: (id: string, days: FunnelCampaignDays) =>
        api.post<FunnelCampaignsReport>(`/admin/funnels/${encodeURIComponent(id)}/campaigns/refresh?days=${days}`, {}),
      layout: (id: string) => api.get<FunnelLayoutState>(`/admin/funnels/${encodeURIComponent(id)}/layout`),
      saveLayout: (id: string, blocks: Layout) =>
        api.put<FunnelLayoutState>(`/admin/funnels/${encodeURIComponent(id)}/layout`, { blocks }),
      resetLayout: (id: string) => api.delete<FunnelLayoutState>(`/admin/funnels/${encodeURIComponent(id)}/layout`),
      // Pages lined up to test next (no visitors until started).
      addDraft: (id: string, source: string) => api.post<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/drafts`, { source }),
      deleteDraft: (id: string, draftId: string) =>
        api.delete<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/drafts/${encodeURIComponent(draftId)}`),
      // The draft becomes the next version and takes visitors now (new comparison period).
      // Pause a version (no new visitors) or bring it back; the comparison period carries on.
      setPaused: (id: string, key: string, paused: boolean) =>
        api.patch<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/variants/${encodeURIComponent(key)}`, { paused }),
      startDraft: (id: string, draftId: string) =>
        api.post<FunnelReport>(`/admin/funnels/${encodeURIComponent(id)}/drafts/${encodeURIComponent(draftId)}/start`, {}),
      // Keputusan AI: the last run, the changes made and the settings; a run now; the Claude Code lane;
      // the operator's answer to a card and "Buat asal"; AI ambil alih (Override) and its numbers.
      ai: (id: string) => api.get<FunnelAiState>(`/admin/funnels/${encodeURIComponent(id)}/ai`),
      aiRun: (id: string) => api.post<FunnelAiState & { report: FunnelReport }>(`/admin/funnels/${encodeURIComponent(id)}/ai/run`, {}, { timeoutMs: 240_000 }),
      aiBrief: (id: string) => api.get<{ text: string; cards: number }>(`/admin/funnels/${encodeURIComponent(id)}/ai/brief`),
      aiExternal: (id: string, body: FunnelAiExternalInput) =>
        api.post<FunnelAiState & { report: FunnelReport }>(`/admin/funnels/${encodeURIComponent(id)}/ai/external`, body, { timeoutMs: 240_000 }),
      aiDecide: (id: string, cardId: string, approve: boolean) =>
        api.post<FunnelAiState & { report: FunnelReport | null }>(
          `/admin/funnels/${encodeURIComponent(id)}/ai/cards/${encodeURIComponent(cardId)}`,
          { approve },
          { timeoutMs: 240_000 },
        ),
      aiUndo: (id: string, cardId: string) =>
        api.post<FunnelAiState & { report: FunnelReport }>(
          `/admin/funnels/${encodeURIComponent(id)}/ai/cards/${encodeURIComponent(cardId)}/undo`,
          {},
          { timeoutMs: 60_000 },
        ),
      aiSettings: (id: string, body: FunnelAiSettingsInput) => api.put<FunnelAiState>(`/admin/funnels/${encodeURIComponent(id)}/ai/settings`, body, { timeoutMs: 60_000 }),
      aiTakeover: (id: string, body: FunnelAiTakeoverInput) => api.put<FunnelAiState>(`/admin/funnels/${encodeURIComponent(id)}/ai/takeover`, body, { timeoutMs: 60_000 }),
      // AI belajar now: marks for changes whose 24 or 72 hours have passed.
      aiScore: (id: string) => api.post<FunnelAiState & { scored: number }>(`/admin/funnels/${encodeURIComponent(id)}/ai/score`, {}, { timeoutMs: 120_000 }),
      // The cron's turn for this funnel, now (it still waits for new data).
      aiTick: (id: string) =>
        api.post<FunnelAiState & { ran: boolean; reason: string; report: FunnelReport }>(`/admin/funnels/${encodeURIComponent(id)}/ai/tick`, {}, {
          timeoutMs: 240_000,
        }),
    },
    monetize: {
      /** 127B S6: every advertiser's ad coupon funnel. */
      kuponFunnel: (days: number) => api.get<import("@shared/adCoupon").KuponFunnelView>(`/admin/monetize/kupon-funnel?days=${days}`),
      /** Pengiklan by invitation (117C S8). */
      advertisers: () => api.get<{ rows: import("@shared/iklanAdv").AdvAdminRow[] }>("/admin/monetize/advertisers"),
      inviteAdvertiser: (body: { email: string; businessName?: string }) => api.post<{ id: string; sent: boolean }>("/admin/monetize/advertisers/invite", body),
      resendInvite: (id: string) => api.post<{ id: string; sent: boolean }>(`/admin/monetize/advertisers/invites/${id}/resend`, {}),
      revokeInvite: (id: string) => api.delete<{ revoked: true }>(`/admin/monetize/advertisers/invites/${id}`),
      advertiserStatus: (userId: string, status: "active" | "suspended") => api.post<{ status: string }>(`/admin/monetize/advertisers/${userId}/status`, { status }),
      settings: () => api.get<IklanSettings>("/admin/monetize/settings"),
      saveSettings: (body: {
        hqStores: string[];
        holdoutPct: number;
        blocks?: IklanSettings["blocks"];
        floors?: IklanSettings["floors"];
        houseCpm?: IklanSettings["houseCpm"];
        managed?: IklanSettings["managed"];
        tiket?: IklanSettings["tiket"];
        affiliateCpa?: number;
        clickOpens?: IklanSettings["clickOpens"];
        placements?: IklanSettings["placements"];
        frequency?: IklanSettings["frequency"];
        pembantuCategories?: IklanSettings["pembantuCategories"];
        twoMode?: boolean;
        topup?: IklanSettings["topup"];
      }) =>
        api.put<IklanSettings>("/admin/monetize/settings", body),
      managed: (days = 7) => api.get<ManagedOverview>(`/admin/monetize/managed?days=${days}`),
      overview: (julat: string) => api.get<MonetizeOverview>(`/admin/monetize/overview?${julat}`),
      funnel: (julat: string) => api.get<MonetizeFunnel>(`/admin/monetize/funnel?${julat}`),
      settleManaged: () => api.post<{ campaigns: number; billedCents: number; paused: number }>("/admin/monetize/managed/settle", {}),
      retryManaged: (id: string) => api.post<{ state: string; error: string | null }>(`/admin/monetize/managed/${encodeURIComponent(id)}/retry`, {}),
      stores: () => api.get<{ stores: MonetizeStore[] }>("/admin/monetize/stores"),
      offers: (days = 7) => api.get<{ days: number; offers: MonetizeOffer[] }>(`/admin/monetize/offers?days=${days}`),
      campaigns: () => api.get<{ campaigns: MonetizeCampaign[] }>("/admin/monetize/campaigns"),
      kredit: () => api.get<{ codes: CreditCodeView[]; hq: HqMeter }>("/admin/monetize/kredit"),
      createKredit: (body: { amount: number; mode: CreditMode; storeId?: string; maxUses: number; creditDays: number; validUntil: string | null; purpose: string }) =>
        api.post<{ id: string; code: string }>("/admin/monetize/kredit", body),
      setKreditDisabled: (id: string, disabled: boolean) => api.patch<{ id: string; disabled: boolean }>(`/admin/monetize/kredit/${encodeURIComponent(id)}`, { disabled }),
      sweepKredit: () => api.post<{ lapsed: number; warned: number }>("/admin/monetize/kredit/sweep", {}),
      kreditStores: (q: string) => api.get<{ stores: { id: string; name: string; subdomain: string | null }[] }>(`/admin/monetize/kredit/stores?q=${encodeURIComponent(q)}`),
      deleteCampaigns: (ids: string[]) => api.post<BulkResult>("/admin/monetize/campaigns/delete", { ids }),
      restoreCampaigns: (ids: string[]) => api.post<BulkResult>("/admin/monetize/campaigns/restore", { ids }),
      deleteOffers: (ids: string[]) => api.post<BulkResult>("/admin/monetize/offers/delete", { ids }),
      restoreOffers: (ids: string[]) => api.post<BulkResult>("/admin/monetize/offers/restore", { ids }),
      products: (q: string, owner: MonetizeOwner = "all") =>
        api.get<{ products: MonetizeProduct[] }>(`/admin/monetize/products?q=${encodeURIComponent(q)}&owner=${owner}`),
      addOffer: (body: MonetizeOfferInput) => api.post<{ id: string }>("/admin/monetize/offers", body),
      updateOffer: (id: string, body: Partial<MonetizeOfferInput> & { status?: "active" | "paused"; placements?: string[] | null }) =>
        api.patch<{ id: string }>(`/admin/monetize/offers/${id}`, body),
      lift: (days = 14) => api.get<LiftReport & { days: number }>(`/admin/monetize/lift?days=${days}`),
      auction: (host: string, placement: IklanPlacement = "lepasbeli") =>
        api.get<AuctionView>(`/admin/monetize/auction?host=${encodeURIComponent(host)}&placement=${placement}`),
      settle: () => api.post<{ stores: number; cents: number; byStore: Record<string, number> }>("/admin/monetize/settle", { includeCurrent: true }),
      review: () => api.get<{ campaigns: ReviewItem[] }>("/admin/monetize/review"),
      decide: (id: string, decision: "approve" | "reject", reason?: RejectReason) =>
        api.post<{ id: string; status: string; rejectReason: string | null }>(`/admin/monetize/campaigns/${encodeURIComponent(id)}/review`, { decision, reason }),
    },
    adLinks: {
      list: () => api.get<AdLinksResponse>("/admin/ad-links"),
      create: (body: { label: string; host: string; destPath: string; code?: string }) => api.post<AdLinksResponse>("/admin/ad-links", body),
      update: (code: string, body: { label?: string; campaignId?: string | null; shared?: boolean }) =>
        api.patch<AdLinksResponse>(`/admin/ad-links/${encodeURIComponent(code)}`, body),
      remove: (code: string) => api.delete<AdLinksResponse>(`/admin/ad-links/${encodeURIComponent(code)}`),
    },
    wpImport: {
      // Persist a WordPress import (produced by the local generator runner) as a
      // page on a target store. The runner produces `embedUrl`. `publish: true`
      // publishes it straight away and returns the live `publicUrl`.
      save: (body: WpImportSaveInput) =>
        api.post<{ page: Page; publicUrl: string | null }>("/admin/import/generate", body),
      // Native one-click import (Browser Rendering + Workflow — no local runner).
      // Returns a jobId to poll; works from prod https.
      startAuto: (body: WpImportAutoInput) =>
        api.post<{ jobId: string; slug: string }>("/admin/import/auto", body),
      autoStatus: (jobId: string) =>
        api.get<{ status: WpImportJobStatus }>(
          `/admin/import/auto/status?jobId=${encodeURIComponent(jobId)}`,
        ),
      // Every copy on the CDN (R2 copies persist forever), WordPress imports and
      // hand-built sales pages alike — republish without re-rendering. Removing
      // only hides a row; restore brings it back.
      history: () => api.get<{ imports: WpImport[] }>("/admin/import/history"),
      deleteHistory: (id: string) =>
        api.delete<{ deleted: boolean }>(`/admin/import/history/${id}`),
      restoreHistory: (id: string) =>
        api.post<{ restored: boolean }>(`/admin/import/history/${id}/restore`, {}),
      // Per-store serve-time perf flags (sites/<slug>/perf.json). get resolves
      // every field to a boolean; set saves + purges the copy's edge cache.
      perf: (slug: string) =>
        api.get<{ flags: WpImportPerfInput }>(`/admin/import/perf/${encodeURIComponent(slug)}`),
      setPerf: (slug: string, body: WpImportPerfInput) =>
        api.put<{ flags: WpImportPerfInput; purge: PurgeResult }>(
          `/admin/import/perf/${encodeURIComponent(slug)}`,
          body,
        ),
      // Copies present in R2 with no history row — the History tab cannot
      // reach them, so the console lists them separately.
      orphans: () => api.get<{ slugs: string[] }>("/admin/import/orphans"),
      // Re-encode an already-imported copy's stored images to WebP, one batch
      // per call. Loop while `done` is false, feeding `cursor` back in.
      optimizeImages: (slug: string, cursor?: string) =>
        api.post<{
          done: boolean;
          cursor?: string;
          scanned: number;
          converted: number;
          bytesSaved: number;
          purge?: PurgeResult;
        }>(`/admin/import/optimize/${encodeURIComponent(slug)}`, { cursor }),
      // Undo the conversion: copy every stored backup back over its live key.
      // Same batch/cursor protocol as optimizeImages.
      restoreImages: (slug: string, cursor?: string) =>
        api.post<{
          done: boolean;
          cursor?: string;
          scanned: number;
          converted: number;
          bytesSaved: number;
          purge?: PurgeResult;
        }>(`/admin/import/restore/${encodeURIComponent(slug)}`, { cursor }),
      // Per-store serve-time TEXT overrides (sites/<slug>/text.json) — swap a
      // string in the frozen snapshot without re-importing. `matches[i]` is how
      // many times slot i's anchor still occurs in the copy; 0 = it does nothing.
      text: (slug: string) =>
        api.get<{ slots: WpImportTextSlotInput[]; updatedAt: string | null; matches: number[] }>(
          `/admin/import/text/${encodeURIComponent(slug)}`,
        ),
      // Saving also re-versions the embed URL of every page using this copy
      // (`pagesVersioned`) — a new cache key reaches browsers a purge cannot —
      // and reports whether the edge purge itself succeeded.
      setText: (slug: string, body: WpImportTextInput) =>
        api.put<{
          slots: WpImportTextSlotInput[];
          matches: number[];
          pagesVersioned: number;
          purge: PurgeResult;
        }>(`/admin/import/text/${encodeURIComponent(slug)}`, body),
      checkText: (slug: string, anchor: string) =>
        api.post<{ matches: number }>(`/admin/import/text/${encodeURIComponent(slug)}/check`, { anchor }),
    },
    domains: {
      list: (qs = "") => api.get<AdminDomainList>(`/admin/domains${qs}`),
      audit: () => api.get<AdminDomainAudit>("/admin/domains/audit"),
      legacyRelease: () => api.get<LegacyReleaseReport>("/admin/domains/legacy-release"),
      // Batched on purpose — one live request per domain leaves the Worker, so the
      // console walks its list ADMIN_DOMAIN_PROBE_MAX at a time.
      probe: (domains: string[]) =>
        api.post<DomainHttpProbe[]>("/admin/domains/audit/probe", { domains }),
      create: (body: AdminDomainCreateInput) =>
        api.post<Store & { dnsAutoAdded?: boolean }>("/admin/domains", body),
      test: (storeId: string) =>
        api.post<Store & { dnsAutoAdded?: boolean; zone?: "live" | "legacy" }>(
          `/admin/domains/${storeId}/test`,
          undefined,
        ),
      link: (storeId: string, body: AdminDomainLinkInput) =>
        api.patch<Store>(`/admin/domains/${storeId}/link`, body),
      // The zone move, one deliberate step per call. `start` registers the domain
      // in the live zone and places the pre-validation records but never touches
      // the CNAME; only `cutover` re-points it, and only once Cloudflare reports
      // the new zone active. Splitting them is what keeps a status check from
      // becoming a cutover — the exact conflation behind the 2026-08-18 outage.
      migrateStart: (storeId: string) =>
        api.post<
          Store & {
            migration: AdminDomainMigration | null;
            txtWritten: boolean;
            /** Start re-points the CNAME now, so this is the outcome that matters:
             *  false means the hostname was created but the domain has NOT crossed
             *  over, and the records have to be handed to the seller. */
            cnameMoved: boolean;
            target: string;
          }
        >(`/admin/domains/${storeId}/migrate/start`, undefined),
      migrateStatus: (storeId: string) =>
        api.post<Store & { migration: AdminDomainMigration | null }>(
          `/admin/domains/${storeId}/migrate/status`,
          undefined,
        ),
      migrateCutover: (storeId: string) =>
        api.post<Store & { migration: AdminDomainMigration | null; target: string }>(
          `/admin/domains/${storeId}/migrate/cutover`,
          undefined,
        ),
      migrateFinish: (storeId: string) =>
        api.post<Store & { migration: null; removedFromLegacy: number; prunedRecords: number }>(
          `/admin/domains/${storeId}/migrate/finish`,
          undefined,
        ),
      remove: (storeId: string) => api.delete<{ removed: boolean }>(`/admin/domains/${storeId}`),
      bulkDelete: (body: AdminDomainBulkDeleteInput) =>
        api.post<{ deleted: number; skipped: string[] }>("/admin/domains/bulk-delete", body),
    },
    mcp: {
      overview: () => api.get<AdminMcpOverview>("/admin/mcp"),
      create: (body: AdminMcpKeyCreateInput) => api.post<AdminMcpKeyCreated>("/admin/mcp", body),
      revoke: (id: string) => api.delete<{ revoked: boolean }>(`/admin/mcp/${id}`),
    },
    cfAccounts: {
      list: () => api.get<AdminCfAccount[]>("/admin/cf-accounts"),
      create: (body: AdminCfAccountCreateInput) =>
        api.post<AdminCfAccount>("/admin/cf-accounts", body),
      update: (id: string, body: AdminCfAccountUpdateInput) =>
        api.patch<AdminCfAccount>(`/admin/cf-accounts/${id}`, body),
      remove: (id: string) => api.delete<{ deleted: boolean }>(`/admin/cf-accounts/${id}`),
    },
    orders: {
      list: (qs: string) => api.get<AdminOrdersPage>(`/admin/orders${qs}`),
      subscriptions: (qs: string) =>
        api.get<AdminSubscriptionOrdersPage>(`/admin/orders/subscriptions${qs}`),
      setStatus: (id: string, payment_status: string) =>
        api.patch<Order>(`/admin/orders/${id}`, { payment_status }),
      couponRedemptions: (status = "awaiting_review") =>
        api.get<AdminCouponRedemption[]>(
          `/admin/orders/coupon-redemptions?status=${encodeURIComponent(status)}`,
        ),
      reviewCoupons: (body: CouponReviewInput) =>
        api.post<{ approved: string[]; rejected: string[]; skipped: string[] }>(
          "/admin/orders/coupon-redemptions/review",
          body,
        ),
      couponReceipt: (id: string) =>
        api.blob(`/admin/orders/coupon-redemptions/${id}/receipt`),
    },
    customers: (qs = "") => api.get<AdminSubscribedSellerList>(`/admin/customers${qs}`),
    payments: () => api.get<AdminPaymentsOverview>("/admin/payments"),
    aiTopups: (q: { status?: string; page?: number } = {}) =>
      api.get<import("@shared/buatkan").AdminTopups>(`/admin/payments/ai-topups?status=${encodeURIComponent(q.status ?? "")}&page=${q.page ?? 1}`),
    recheckAiTopup: (id: string) => api.post<{ status: "pending" | "paid" | "failed" }>(`/admin/payments/ai-topups/${encodeURIComponent(id)}/recheck`, {}, { timeoutMs: 30_000 }),
    bulkDelete: (body: AdminBulkDeleteInput) =>
      api.post<{ deleted: number; skipped: string[] }>("/admin/bulk/delete", body),
    moderation: {
      list: (qs: string) => api.get<AdminContentList>(`/admin/moderation${qs}`),
      setPageStatus: (id: string, status: string) =>
        api.post<Page>(`/admin/moderation/pages/${id}`, { status }),
      setProductStatus: (id: string, status: string) =>
        api.post<Product>(`/admin/moderation/products/${id}`, { status }),
    },
    audit: (qs = "") => api.get<AdminAuditList>(`/admin/audit${qs}`),
    coaching: {
      invites: () => api.get<{ invites: import("@shared/coaching").CoachInviteView[] }>("/admin/coaching/invites"),
      createInvite: (body: import("@shared/coaching").CoachInviteInput) => api.post<{ invite: import("@shared/coaching").CoachInviteView }>("/admin/coaching/invites", body),
      createPersonal: (body: { name: string; email: string; phone?: string; cohort?: string | null }) =>
        api.post<{ invite: import("@shared/coaching").CoachInviteView }>("/admin/coaching/invites/personal", body),
      archiveInvite: (id: string, archived: boolean) => api.patch<{ invite: import("@shared/coaching").CoachInviteView }>(`/admin/coaching/invites/${id}`, { archived }),
      products: (q: string) => api.get<{ items: import("@shared/coaching").CoachProductOption[] }>(`/admin/coaching/products?q=${encodeURIComponent(q)}`),
      settings: () => api.get<{ settings: import("@shared/coaching").CoachSettings; spendRm: number }>("/admin/coaching/settings"),
      draft: (id: string) => api.post<{ draft: string }>(`/admin/coaching/queue/${id}/draft`, {}, { timeoutMs: 120_000 }),
      saveSettings: (s: import("@shared/coaching").CoachSettings) => api.put<{ settings: import("@shared/coaching").CoachSettings }>("/admin/coaching/settings", s),
      queue: (filter: "semua" | "coach" | "ai") => api.get<import("@shared/coaching").CoachQueue>(`/admin/coaching/queue?filter=${filter}`),
      reply: (id: string, body: string, asCoach: boolean) => api.post<{ id: string }>(`/admin/coaching/queue/${id}/reply`, { body, asCoach }),
      members: (q: string) => api.get<{ members: import("@shared/coaching").CoachMemberRow[] }>(`/admin/coaching/members?q=${encodeURIComponent(q)}`),
      member: (id: string) => api.get<import("@shared/coaching").CoachMemberCard>(`/admin/coaching/members/${id}`),
      removeMember: (id: string, removed: boolean) => api.post<{ removed: boolean }>(`/admin/coaching/members/${id}/remove`, { removed }),
      hideMessage: (id: string, hidden: boolean) => api.post<{ hidden: boolean }>(`/admin/coaching/messages/${id}/hide`, { hidden }),
    },
    support: {
      list: (qs: string) => api.get<AdminSupportTicket[]>(`/admin/support${qs}`),
      get: (id: string) => api.get<AdminSupportTicketDetail>(`/admin/support/${id}`),
      reply: (id: string, body: SupportMessageInput & { original?: string }) =>
        api.post<SupportMessage>(`/admin/support/${id}/messages`, body),
      setStatus: (id: string, body: SupportStatusInput) =>
        api.patch<SupportTicket>(`/admin/support/${id}`, body),
      uploadImage: async (file: FormData) =>
        api.upload<{ key: string }>(
          "/admin/support/upload-image",
          await maybeOptimizeImageFormData(file),
        ),
      attachment: (ticketId: string, messageId: string) =>
        api.blob(`/admin/support/${ticketId}/messages/${messageId}/attachment`),
      unreadCount: () => api.get<{ count: number }>("/admin/support/unread-count"),
      deskTicket: () => api.get<{ ticket: string; name?: string }>("/admin/support/desk-ticket"),
      inbox: (tab: import("@shared/supportDesk").DeskInboxTab = "staff") => api.get<import("@shared/supportDesk").DeskInboxItem[]>(`/admin/support/inbox?tab=${tab}`),
      take: (id: string) => api.post<{ ok: true }>(`/admin/support/${id}/take`, {}),
      typing: (id: string) => api.post<{ ok: true }>(`/admin/support/${id}/typing`, {}),
      settings: () => api.get<{ settings: import("@shared/supportDesk").DeskSettings; now: import("@shared/supportDesk").PublicDesk; telegramHint?: string | null }>("/admin/support/settings"),
      saveTelegramToken: (token: string | null) => api.put<{ telegramHint: string | null }>("/admin/support/alerts/telegram-token", { token }),
      testAlert: () => api.post<{ emailed: number; recipients: string[]; telegram: { ok: boolean; error: string | null } }>("/admin/support/alerts/test", {}),
      saveSettings: (body: import("@shared/supportDesk").DeskSettings) =>
        api.put<{ settings: import("@shared/supportDesk").DeskSettings; now: import("@shared/supportDesk").PublicDesk }>("/admin/support/settings", body),
      saStores: () => api.get<import("@shared/supportDesk").DeskSaStore[]>("/admin/support/sa-stores"),
      gaps: (all = false) => api.get<import("@shared/supportDesk").DeskGap[]>(`/admin/support/gaps${all ? "?all=1" : ""}`),
      pembantu: (q: { filter?: string; q?: string; before?: string | null }) => {
        const p = new URLSearchParams();
        if (q.filter && q.filter !== "all") p.set("filter", q.filter);
        if (q.q?.trim()) p.set("q", q.q.trim());
        if (q.before) p.set("before", q.before);
        const s = p.toString();
        return api.get<import("@shared/pembantuSessions").PembantuPage>(`/admin/support/pembantu${s ? `?${s}` : ""}`);
      },
      pembantuSession: (key: string) => api.get<import("@shared/pembantuSessions").PembantuDetail>(`/admin/support/pembantu/session?key=${encodeURIComponent(key)}`),
      pembantuThread: (key: string) => api.post<{ threadId: string; created: boolean }>("/admin/support/pembantu/thread", { key }),
      modelTest: (models?: string[]) =>
        api.post<import("@shared/supportModelTest").ModelTestReport>("/admin/support/model-test", models ? { models } : {}, { timeoutMs: 240_000 }),
      templates: () =>
        api.get<{ templates: import("@shared/supportTemplates").SupportTemplate[]; meter: { spentUsd: number; spentRm: number; budgetRm: number; turns: number; byRoute: Record<string, number> } }>("/admin/support/templates"),
      createTemplate: (body: Partial<import("@shared/supportTemplates").SupportTemplate>) => api.post<import("@shared/supportTemplates").SupportTemplate>("/admin/support/templates", body),
      saveTemplate: (id: string, body: Partial<import("@shared/supportTemplates").SupportTemplate>) =>
        api.put<import("@shared/supportTemplates").SupportTemplate>(`/admin/support/templates/${encodeURIComponent(id)}`, body),
      importTemplates: (pack: unknown) => api.post<{ created: number; skipped: number }>("/admin/support/templates/import", pack, { timeoutMs: 60_000 }),
      testTemplate: (body: { question: string; email?: string }) =>
        api.post<import("@shared/supportBot").BotAnswerView>("/admin/support/templates/test", body, { timeoutMs: 60_000 }),
      customerCard: (email: string) =>
        api.get<{ card: import("@shared/customerCard").CustomerCard; text: string }>(`/admin/support/customer-card?email=${encodeURIComponent(email)}`),
      buyer: (id: string) => api.get<import("@shared/supportDesk").DeskBuyer>(`/admin/support/${encodeURIComponent(id)}/buyer`),
      reviewGap: (id: string, open = false) => api.post<{ ok: true }>(`/admin/support/gaps/${encodeURIComponent(id)}/review`, { open }),
      replyCheck: (id: string, body: { message?: string; attachmentKey?: string | null }) =>
        api.post<import("@shared/staffReply").ReplyCheckResult>(`/admin/support/${id}/reply-check`, body, { timeoutMs: 30_000 }),
      turn: (turnId: string) => api.get<import("@shared/supportSignals").TurnExplain>(`/admin/support/turns/${encodeURIComponent(turnId)}`),
      prestasi: (days: number) => api.get<import("@shared/supportPrestasi").PrestasiBoard>(`/admin/support/prestasi?days=${days}`),
      prestasiItems: (q: { channel: import("@shared/supportPrestasi").PrestasiChannel; tag: string | null; days: number }) =>
        api.get<import("@shared/supportPrestasi").PrestasiItem[]>(`/admin/support/prestasi/items?channel=${q.channel}&days=${q.days}${q.tag ? `&tag=${q.tag}` : ""}`),
      addMarker: (label: string) => api.post<{ ok: true }>("/admin/support/prestasi/markers", { label }),
      knowledge: () => api.get<import("@shared/supportKnowledge").KnowledgeEntry[]>("/admin/support/knowledge"),
      addKnowledge: (body: { title: string; body: string; audience: string; status: string }) => api.post<import("@shared/supportKnowledge").KnowledgeEntry>("/admin/support/knowledge", body),
      saveKnowledge: (id: string, body: { title: string; body: string; audience: string; status: string }) => api.put<import("@shared/supportKnowledge").KnowledgeEntry>(`/admin/support/knowledge/${encodeURIComponent(id)}`, body),
      deleteKnowledge: (id: string) => api.delete<{ ok: true }>(`/admin/support/knowledge/${encodeURIComponent(id)}`),
      draftKnowledge: () => api.post<{ made: number; queued?: number }>("/admin/support/knowledge/draft", {}, { timeoutMs: 120_000 }),
      // 120A: Pengetahuan from real conversations, a queue the cron works through.
      knowledgeJobs: () => api.get<import("@shared/knowledgeJobs").JobSummary[]>("/admin/support/knowledge/jobs"),
      knowledgeReviewLeft: () => api.get<{ left: number }>("/admin/support/knowledge/review"),
      benchRuns: () => api.get<import("@shared/supportBench").BenchRun[]>("/admin/support/bench"),
      runBench: () => api.post<import("@shared/supportBench").BenchRun | { stopped: "budget" | "empty" }>("/admin/support/bench", {}, { timeoutMs: 300_000 }),
      reviewKnowledge: () =>
        api.post<{ reviewed: number; activated: number; dropped: number; left: number; stopped: "budget" | "no_ai" | null }>("/admin/support/knowledge/review", {}, { timeoutMs: 300_000 }),
      importKnowledgeBundle: (bundle: unknown) => api.post<{ ok: true; queued: number; skipped: number; topics: number }>("/admin/support/knowledge/jobs/import", bundle, { timeoutMs: 60_000 }),
      // One topic through the AI (up to 90 s), well past the 20 s default.
      runKnowledgeJob: (id?: string) =>
        api.post<{ ran: number; made: number; left: number; stopped: "budget" | "no_ai" | null }>("/admin/support/knowledge/jobs/run", id ? { id } : {}, { timeoutMs: 180_000 }),
      sendDigest: () => api.post<{ emailed: number; telegram: boolean; lines: string[]; worst: number }>("/admin/support/digest/send", {}),
      planCredits: () => api.get<import("@shared/buatkan").PlanCreditsRow[]>("/admin/support/plan-credits"),
      setPlanCredits: (plan: string, credits: number | null) => api.put<import("@shared/buatkan").PlanCreditsRow | null>(`/admin/support/plan-credits/${encodeURIComponent(plan)}`, { credits }),
      backfillPlanCredits: () =>
        api.post<{ granted: number; already: number; lapsed: number; credits: number }>("/admin/support/plan-credits/backfill", {}, { timeoutMs: 120_000 }),
      quality: (days: 7 | 30) => api.get<import("@shared/supportQuality").QualityBoard>(`/admin/support/quality?days=${days}`),
      review: (turnId: string, body: { verdict: "ok" | "wrong"; rightTemplate?: string | null; saveCase?: boolean }) =>
        api.post<{ paused: string | null; caseId: string | null }>(`/admin/support/turns/${encodeURIComponent(turnId)}/review`, body),
      resumeTemplate: (id: string) => api.post<{ ok: true }>(`/admin/support/templates/${encodeURIComponent(id)}/resume`, {}),
      runRegression: () =>
        api.post<{ passed: number; failed: number; results: import("@shared/supportQuality").RegressionResult[]; costUsd: number }>("/admin/support/regression/run", {}, { timeoutMs: 120_000 }),
      draftSuggestion: (key: string) => api.post<import("@shared/supportTemplates").SupportTemplate>("/admin/support/suggestions/draft", { key }),
      dismissSuggestion: (key: string) => api.post<{ ok: true }>("/admin/support/suggestions/dismiss", { key }),
      // 118W B: the AI writes two fixes one after the other (up to 60 s each) — well past the 20 s default.
      runFixes: () => api.post<{ created: number; waiting: number; stopped: "budget" | "no_ai" | null }>("/admin/support/fixes/run", {}, { timeoutMs: 180_000 }),
      approveFix: (id: string) => api.post<{ ok: true; templateId: string }>(`/admin/support/fixes/${encodeURIComponent(id)}/approve`, {}),
      draftFix: (id: string) => api.post<{ templateId: string }>(`/admin/support/fixes/${encodeURIComponent(id)}/draft`, {}),
      rejectFix: (id: string) => api.post<{ ok: true }>(`/admin/support/fixes/${encodeURIComponent(id)}/reject`, {}),
      sendReport: () => api.post<{ sent: number }>("/admin/support/quality/report", {}, { timeoutMs: 30_000 }),
      decideEmailFix: (id: string, fixId: string, approve: boolean) =>
        api.post<import("@shared/emailFix").EmailFixView>(`/admin/support/${id}/email-fix/${encodeURIComponent(fixId)}`, { approve }, { timeoutMs: 45_000 }),
    },
    affiliates: {
      list: () => api.get<AdminAffiliatesResponse>("/admin/affiliates"),
      setStatus: (id: string, body: AdminAffiliateStatusInput) =>
        api.post<{ id: string; status: string }>(`/admin/affiliates/${id}/status`, body),
      approveCommissions: (id: string) =>
        api.post<{ approved: number }>(`/admin/affiliates/${id}/approve-commissions`),
      payout: (body: AdminAffiliatePayoutInput) =>
        api.post<{ paidCount: number; paidCents: number }>("/admin/affiliates/payout", body),
    },
    platform: {
      settings: () => api.get<AdminPlatformSettings>("/admin/platform/settings"),
      // Plan landing-page library (the super-admin's own pages, flagged in/out).
      landingPages: () => api.get<LandingLibraryPage[]>("/admin/platform/landing-pages"),
      setLandingPage: (id: string, isPlanLanding: boolean) =>
        api.patch<PlatformSettings>(`/admin/platform/landing-pages/${id}`, { isPlanLanding }),
      updateSettings: (body: PlatformSettingsUpdateInput) =>
        api.patch<AdminPlatformSettings>("/admin/platform/settings", body),
      plans: () => api.get<PlanConfig[]>("/admin/platform/plans"),
      createPlan: (body: PlanConfigCreateBody) =>
        api.post<PlanConfig>("/admin/platform/plans", body),
      updatePlan: (plan: string, body: PlanConfigUpdateInput) =>
        api.patch<PlanConfig>(`/admin/platform/plans/${plan}`, body),
      deletePlan: (plan: string) =>
        api.delete<{ deleted: boolean }>(`/admin/platform/plans/${plan}`),
      // A package's assigned starter pages (cloned into a buyer's store when they
      // register for THIS package).
      planPages: (plan: string) =>
        api.get<{ pageId: string; name: string | null }[]>(`/admin/platform/plans/${plan}/pages`),
      setPlanPages: (plan: string, pageIds: string[]) =>
        api.put<{ count: number }>(`/admin/platform/plans/${plan}/pages`, { pageIds }),
      revenue: () => api.get<AdminPlanRevenue>("/admin/platform/revenue"),
      coupons: {
        list: () => api.get<PlatformCoupon[]>("/admin/platform/coupons"),
        create: (body: PlatformCouponCreateInput) =>
          api.post<PlatformCoupon>("/admin/platform/coupons", body),
        update: (id: string, body: PlatformCouponUpdateInput) =>
          api.patch<PlatformCoupon>(`/admin/platform/coupons/${id}`, body),
        remove: (id: string) =>
          api.delete<{ deleted: boolean }>(`/admin/platform/coupons/${id}`),
      },
      projects: {
        list: () => api.get<PlanProject[]>("/admin/platform/projects"),
        create: (body: PlanProjectCreateBody) =>
          api.post<PlanProject>("/admin/platform/projects", body),
        update: (slug: string, body: PlanProjectUpdateInput) =>
          api.patch<PlanProject>(`/admin/platform/projects/${slug}`, body),
        remove: (slug: string) =>
          api.delete<{ deleted: boolean }>(`/admin/platform/projects/${slug}`),
        duplicate: (slug: string, body: PlanProjectDuplicateBody) =>
          api.post<{ project: PlanProject; clonedPackages: number }>(
            `/admin/platform/projects/${slug}/duplicate`,
            body,
          ),
      },
    },
  },
};
